import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  CaretLeft,
  Plus,
  UsersThree,
  PaperPlaneTilt,
  ListBullets,
  ArrowsClockwise,
  PencilSimple,
  Image as ImageIcon,
  Lock,
  LockOpen,
  Crown,
  UserPlus,
  UserMinus,
  Link as LinkIcon,
  Trash,
  DownloadSimple,
  CheckCircle,
  Gear,
  Copy,
  Hash,
  Users,
  Funnel,
  Sparkle,
  QrCode,
  UploadSimple,
  Printer,
  ChartBar,
  Lightning,
  PlugsConnected,
} from '@phosphor-icons/react';
import {
  PageHeader,
  Surface,
  Button,
  Input,
  Textarea,
  Label,
  DateTimePicker,
  Badge,
  Modal,
  EmptyState,
  Spinner,
  useToast,
} from '@/components/ui-kit';
import { api, PUBLIC_BASE_URL } from '@/lib/api';
import { apiErrorMessage, fmtDate, parsePhoneList, shortJid, cn, statusColor } from '@/lib/utils';

const TABS = [
  { id: 'overview', label: 'Visão Geral', icon: ChartBar },
  { id: 'groups', label: 'Grupos', icon: UsersThree },
  { id: 'messages', label: 'Mensagens', icon: PaperPlaneTilt },
  { id: 'automations', label: 'Automações', icon: Lightning },
  { id: 'sendhook', label: 'Sendhook', icon: PlugsConnected },
  { id: 'actions', label: 'Ações', icon: Sparkle },
  { id: 'logs', label: 'Logs', icon: ListBullets },
];

export default function CampaignDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [camp, setCamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [showInstanceModal, setShowInstanceModal] = useState(false);
  const { show, node: toastNode } = useToast();

  const refresh = async () => {
    try {
      const c = await api.getCampaign(id);
      setCamp(c);
    } catch (e) {
      if (e?.response?.status === 404) nav('/campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, [id]);

  if (loading || !camp) {
    return (
      <div className="flex items-center gap-2 text-zinc-400">
        <Spinner /> carregando campanha…
      </div>
    );
  }

  return (
    <div className="min-w-0" data-testid="page-campaign-detail">
      <Link
        to="/campaigns"
        className="text-xs font-mono uppercase tracking-[0.15em] text-zinc-400 hover:text-lime-400 flex items-center gap-1 mb-4"
        data-testid="back-to-campaigns"
      >
        <CaretLeft size={12} /> voltar para campanhas
      </Link>

      <PageHeader
        badge={`// ${camp.instanceName}`}
        title={camp.name}
        subtitle={camp.description || 'Sem descrição.'}
        actions={
          <>
            <Button variant="secondary" onClick={() => setShowInstanceModal(true)} data-testid="change-campaign-instance-btn">
              <PlugsConnected size={14} />
              Trocar instância
            </Button>
            <Button variant="secondary" onClick={refresh} data-testid="refresh-campaign-btn">
              <ArrowsClockwise size={14} />
              Atualizar
            </Button>
          </>
        }
      />

      {/* Tabs */}
      <div className="-mx-4 mb-6 flex gap-1 overflow-x-auto border-b border-zinc-700 px-4 sm:mx-0 sm:px-0">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          const count =
            t.id === 'groups' ? (camp.groups || []).length :
            t.id === 'messages' ? (camp.dispatches || []).length :
            t.id === 'actions' ? (camp.dispatches || []).length :
            t.id === 'logs' ? (camp.dispatches || []).length : null;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={cn(
                'mb-[-1px] flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm transition-colors',
                active
                  ? 'border-lime-500 text-zinc-50'
                  : 'border-transparent text-zinc-400 hover:text-zinc-200'
              )}
            >
              <Icon size={14} weight={active ? 'fill' : 'regular'} />
              {t.label}
              {count !== null && (
                <span className="text-[10px] font-mono text-zinc-400">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'overview' && <OverviewTab camp={camp} notify={show} />}
      {tab === 'groups' && (
        <GroupsTab camp={camp} onChange={refresh} notify={show} />
      )}
      {tab === 'messages' && (
        <MessagesTab camp={camp} onChange={refresh} notify={show} setTab={setTab} />
      )}
      {tab === 'automations' && <AutomationsTab camp={camp} notify={show} />}
      {tab === 'sendhook' && <SendhookTab camp={camp} onChange={refresh} notify={show} />}
      {tab === 'actions' && <ActionsTab camp={camp} onChange={refresh} notify={show} setTab={setTab} />}
      {tab === 'logs' && <LogsTab camp={camp} />}

      {showInstanceModal && (
        <CampaignInstanceModal
          camp={camp}
          onClose={() => setShowInstanceModal(false)}
          onDone={() => {
            setShowInstanceModal(false);
            show('Instância da campanha atualizada', 'success');
            refresh();
          }}
          onError={(m) => show(m, 'error')}
        />
      )}

      {toastNode}
    </div>
  );
}

function CampaignInstanceModal({ camp, onClose, onDone, onError }) {
  const [instances, setInstances] = useState([]);
  const [instanceName, setInstanceName] = useState(camp.instanceName || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.listInstances()
      .then((rows) => {
        if (alive) setInstances(rows || []);
      })
      .catch((e) => onError(`Erro: ${e?.response?.data?.detail || e.message}`));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line
  }, []);

  const submit = async () => {
    if (!instanceName) return onError('Escolha uma instância');
    setBusy(true);
    try {
      await api.updateCampaign(camp.id, { instanceName });
      onDone();
    } catch (e) {
      onError(`Erro: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Trocar instância da campanha"
      testId="campaign-instance-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || !instanceName}>{busy ? <Spinner /> : 'Salvar instância'}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <Label>Instância atual</Label>
          <div className="text-sm font-mono text-zinc-300 bg-zinc-950 border border-zinc-700 px-3 py-2">{camp.instanceName}</div>
        </div>
        <div>
          <Label>Nova instância</Label>
          <select value={instanceName} onChange={(e) => setInstanceName(e.target.value)} className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm px-3 py-2 rounded-sm font-mono">
            <option value="">Selecione</option>
            {instances.map((inst) => (
              <option key={inst.instanceName} value={inst.instanceName}>
                {inst.instanceName} - {inst.connectionStatus || 'sem status'}
              </option>
            ))}
          </select>
        </div>
        <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 p-3 rounded-sm">
          O novo número precisa ser administrador dos grupos da campanha para trocar nome, descrição, imagem, configurações, admins e participantes. O sistema apenas muda o vínculo operacional da campanha.
        </div>
      </div>
    </Modal>
  );
}

/* ========================== OVERVIEW TAB ========================== */
function OverviewTab({ camp }) {
  const [days, setDays] = useState(1);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await api.campaignMetrics(camp.id, days);
        if (alive) setMetrics(data);
      } catch {
        if (alive) setMetrics(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetch();
    return () => {
      alive = false;
    };
  }, [camp.id, days]);

  const groups = camp.groups || [];
  const totalLimit = groups.reduce((sum, g) => sum + Number(g.limit || camp.groupSettings?.participantLimit || 0), 0);
  const currentMembers = metrics?.currentMembers ?? groups.reduce((sum, g) => sum + Number(g.size || 0), 0);
  const occupancy = totalLimit ? Math.round((currentMembers / totalLimit) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-lg text-zinc-50">Métricas da campanha</h3>
          <p className="text-xs text-zinc-400 font-mono">hits, entradas e saídas capturados pelo funil e webhook</p>
        </div>
        <div className="flex gap-1 border border-zinc-700 bg-zinc-950 p-1 rounded-sm">
          {[1, 7, 15, 28].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={cn(
                'px-3 py-1.5 text-xs font-mono rounded-sm transition-colors',
                days === d ? 'bg-lime-500 text-zinc-950' : 'text-zinc-400 hover:text-zinc-100'
              )}
            >
              {d === 1 ? 'Dia' : `${d} dias`}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-400 flex items-center gap-2"><Spinner /> carregando métricas…</div>
      ) : (
        <div className="grid grid-cols-2 xl:grid-cols-5 gap-3">
          <CampaignMetric label="Clicks / hits" value={metrics?.hits || 0} hint={`${days === 1 ? 'hoje' : `${days} dias`}`} />
          <CampaignMetric label="Leads entraram" value={metrics?.entered || 0} hint="webhook / tamanho" />
          <CampaignMetric label="Leads saíram" value={metrics?.left || 0} hint="via webhook" />
          <CampaignMetric label="% entrada" value={`${metrics?.entryRate || 0}%`} hint="entradas / hits" />
          <CampaignMetric label="% saída" value={`${metrics?.exitRate || 0}%`} hint="saídas / entradas" />
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Surface className="xl:col-span-2 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-bold text-lg text-zinc-50">Série diária</h3>
              <p className="text-xs text-zinc-400 font-mono">comparativo por dia do período selecionado</p>
            </div>
            <Badge variant="info">{days}d</Badge>
          </div>
          <div className="space-y-3">
            {(metrics?.buckets || []).map((row) => {
              const max = Math.max(...(metrics?.buckets || []).flatMap((b) => [b.hits, b.entered, b.left]), 1);
              return (
                <div key={row.date} className="grid grid-cols-12 gap-3 items-center text-xs font-mono">
                  <span className="col-span-3 text-zinc-400">{row.date.slice(5)}</span>
                  <MiniMetricBar className="col-span-3" label="hits" value={row.hits} max={max} color="bg-lime-500" />
                  <MiniMetricBar className="col-span-3" label="entrou" value={row.entered} max={max} color="bg-emerald-400" />
                  <MiniMetricBar className="col-span-3" label="saiu" value={row.left} max={max} color="bg-red-400" />
                </div>
              );
            })}
          </div>
        </Surface>

        <Surface className="p-5">
          <h3 className="font-display font-bold text-lg text-zinc-50">Capacidade</h3>
          <p className="text-xs text-zinc-400 font-mono mb-4">membros atuais nos grupos da campanha</p>
          <div className="font-display font-black text-4xl text-zinc-50">{currentMembers}</div>
          <p className="text-xs text-zinc-400 font-mono mt-1">de {totalLimit || '—'} vagas mapeadas</p>
          <div className="h-2 bg-zinc-900 rounded-sm overflow-hidden mt-4">
            <div className="h-full bg-lime-500" style={{ width: `${Math.min(100, occupancy)}%` }} />
          </div>
          <p className="text-xs text-zinc-400 font-mono mt-2">{occupancy}% ocupação</p>
        </Surface>
      </div>

      <Surface className="p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="font-display font-bold text-lg text-zinc-50">Canais do funil</h3>
            <p className="text-xs text-zinc-400 font-mono">origem capturada por src, utm_source ou source no link público</p>
          </div>
          <Badge variant="info">{metrics?.channels?.length || 0} canais</Badge>
        </div>
        {(metrics?.channels || []).length === 0 ? (
          <div className="border border-dashed border-zinc-700 bg-zinc-950/40 p-5 text-sm text-zinc-400 font-mono">
            Nenhum canal registrado no período selecionado.
          </div>
        ) : (
          <div className="space-y-3">
            {(metrics?.channels || []).map((channel) => (
              <ChannelRow key={channel.source} channel={channel} total={metrics?.hits || 0} />
            ))}
          </div>
        )}
      </Surface>
    </div>
  );
}

/* ========================== SENDHOOK TAB ========================== */
function SendhookTab({ camp, onChange, notify }) {
  const cfg = camp.sendhook || {};
  const [enabled, setEnabled] = useState(Boolean(cfg.enabled));
  const [url, setUrl] = useState(cfg.url || '');
  const [secret, setSecret] = useState(cfg.secret || '');
  const [events, setEvents] = useState(cfg.events || ['entered']);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    const next = camp.sendhook || {};
    setEnabled(Boolean(next.enabled));
    setUrl(next.url || '');
    setSecret(next.secret || '');
    setEvents(next.events || ['entered']);
  }, [camp.id, camp.sendhook]);

  const toggleEvent = (event) => {
    setEvents((prev) => (
      prev.includes(event)
        ? prev.filter((item) => item !== event)
        : [...prev, event]
    ));
  };

  const saveConfig = async () => {
    return api.updateCampaignSendhook(camp.id, { enabled, url, secret, events });
  };

  const previewEvent = events.includes('entered') ? 'entered' : 'left';

  const save = async () => {
    setSaving(true);
    try {
      await saveConfig();
      notify('Sendhook salvo', 'success');
      onChange?.();
    } catch (e) {
      notify(apiErrorMessage(e, 'Erro ao salvar Sendhook'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      if (!url.trim()) {
        notify('Informe a URL antes de testar', 'error');
        return;
      }
      await saveConfig();
      const result = await api.testCampaignSendhook(camp.id, { event: previewEvent });
      notify(`Teste enviado: HTTP ${result.statusCode}`, 'success');
      onChange?.();
    } catch (e) {
      notify(apiErrorMessage(e, 'Erro ao testar Sendhook'), 'error');
    } finally {
      setTesting(false);
    }
  };

  const sample = {
    event: previewEvent === 'left' ? 'lead_left' : 'lead_entered',
    eventKind: previewEvent,
    campaignId: camp.id,
    campaignName: camp.name,
    groupName: '#10 Clube do Glow',
    leadPhone: '5599999999999',
    participantJid: '5599999999999@s.whatsapp.net',
    source: 'convite3',
    utm: { src: 'convite3' },
    occurredAt: nowPreview(),
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <Surface className="xl:col-span-2 p-5 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display font-bold text-xl text-zinc-50">Sendhook da campanha</h3>
            <p className="text-sm text-zinc-400 mt-1">
              Envie entradas e saídas dos grupos para Sheets, n8n, Make ou outro webhook.
            </p>
          </div>
          <Badge variant={enabled ? 'success' : 'warning'}>{enabled ? 'ativo' : 'inativo'}</Badge>
        </div>

        <label className="flex items-center gap-3 text-sm text-zinc-200 border border-zinc-800 bg-zinc-950/40 p-3 rounded-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 accent-lime-500"
          />
          Ativar envio automático desta campanha
        </label>

        <div>
          <Label>URL do webhook</Label>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://hooks.zapier.com/... ou https://seu-n8n/webhook/..."
            data-testid="sendhook-url"
          />
        </div>

        <div>
          <Label>Token secreto opcional</Label>
          <Input
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="Será enviado no header X-Simplific-Token"
            data-testid="sendhook-secret"
          />
        </div>

        <div>
          <Label>Eventos enviados</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { id: 'entered', title: 'Entrou no grupo', hint: 'quando o lead aparece como participante' },
              { id: 'left', title: 'Saiu do grupo', hint: 'quando o lead sai ou é removido' },
            ].map((item) => (
              <label key={item.id} className="border border-zinc-800 bg-zinc-950/40 p-3 rounded-sm flex gap-3">
                <input
                  type="checkbox"
                  checked={events.includes(item.id)}
                  onChange={() => toggleEvent(item.id)}
                  className="mt-1 h-4 w-4 accent-lime-500"
                />
                <span>
                  <span className="block text-sm text-zinc-100 font-semibold">{item.title}</span>
                  <span className="block text-xs text-zinc-500">{item.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={saving} data-testid="save-sendhook">
            {saving ? <Spinner /> : 'Salvar Sendhook'}
          </Button>
          <Button variant="secondary" onClick={test} disabled={testing || !url} data-testid="test-sendhook">
            {testing ? <Spinner /> : 'Salvar e testar'}
          </Button>
        </div>
      </Surface>

      <Surface className="p-5">
        <h3 className="font-display font-bold text-lg text-zinc-50">Payload enviado</h3>
        <p className="text-xs text-zinc-400 font-mono mb-4">
          Exemplo do JSON que chega no webhook.
        </p>
        <pre className="text-[11px] leading-relaxed whitespace-pre-wrap break-words bg-zinc-950 border border-zinc-800 p-3 rounded-sm text-lime-200">
          {JSON.stringify(sample, null, 2)}
        </pre>
      </Surface>
    </div>
  );
}

function nowPreview() {
  return new Date().toISOString();
}

function CampaignMetric({ label, value, hint }) {
  return (
    <Surface className="p-4">
      <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400">{label}</p>
      <p className="font-display font-black text-3xl text-zinc-50 mt-2">{value}</p>
      <p className="text-[10px] font-mono text-zinc-500 mt-1">{hint}</p>
    </Surface>
  );
}

function MiniMetricBar({ className, label, value, max, color }) {
  const pct = Math.round((Number(value || 0) / Math.max(max, 1)) * 100);
  return (
    <div className={className}>
      <div className="flex justify-between text-[10px] text-zinc-400 mb-1">
        <span>{label}</span>
        <span>{value || 0}</span>
      </div>
      <div className="h-1.5 bg-zinc-900 rounded-sm overflow-hidden">
        <div className={cn('h-full', color)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ChannelRow({ channel, total }) {
  const hits = Number(channel.hits || 0);
  const entered = Number(channel.entered || 0);
  const left = Number(channel.left || 0);
  const share = Number(channel.share || 0);
  const pct = Math.round((hits / Math.max(Number(total || 0), 1)) * 100);
  const entryRate = Number(channel.entryRate || 0);
  const attributedEntries = Number(channel.attributedEntries || 0);
  const sourceLabel = channel.source === 'sem-origem' ? 'sem origem identificada' : channel.source || 'direct';
  return (
    <div className="grid grid-cols-12 gap-3 items-center text-xs font-mono border border-zinc-800 bg-zinc-950/40 px-3 py-3 rounded-sm">
      <div className="col-span-4 min-w-0">
        <div className="text-zinc-100 truncate">{sourceLabel}</div>
        <div className="text-[10px] text-zinc-500">canal</div>
      </div>
      <div className="col-span-5">
        <div className="h-2 bg-zinc-900 rounded-sm overflow-hidden">
          <div className="h-full bg-lime-500" style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2 text-[10px] text-zinc-400">
          <span>{hits} cliques</span>
          <span className="text-emerald-300">{entered} entradas</span>
          <span className="text-red-300">{left} saídas</span>
        </div>
      </div>
      <div className="col-span-3 text-right">
        <div className="text-zinc-50">{entryRate}% conversão</div>
        <div className="text-[10px] text-zinc-500">{share}% dos cliques</div>
        {entered > 0 && attributedEntries < entered ? (
          <div className="text-[10px] text-amber-300">{attributedEntries}/{entered} atribuídas</div>
        ) : null}
      </div>
    </div>
  );
}

/* ========================== GROUPS TAB (Funnel Mode) ========================== */
function groupOperationalStatus(group, settings) {
  const size = Number(group.size || 0);
  const limit = Number(group.limit || settings?.participantLimit || 200);
  const pct = Math.min(100, Math.round((size / Math.max(1, limit)) * 100));
  if (group.isActive === false || group.manualClosed) {
    return { key: 'closed', label: 'fechado', dot: 'bg-zinc-500', badge: 'default', pct };
  }
  if (group.isFull || size >= limit) {
    return { key: 'full', label: 'lotado', dot: 'bg-red-500', badge: 'danger', pct };
  }
  if (pct >= 80) {
    return { key: 'near', label: 'atenção', dot: 'bg-amber-400', badge: 'warn', pct };
  }
  return { key: 'available', label: 'disponível', dot: 'bg-lime-500', badge: 'success', pct };
}

function GroupsTab({ camp, onChange, notify }) {
  const [showSettings, setShowSettings] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);
  const [provisionCount, setProvisionCount] = useState('');

  const settings = camp.groupSettings;
  const groups = (camp.groups || []).slice().sort((a, b) => (a.counter || 0) - (b.counter || 0));
  const publicUrl = camp.publicSlug
    ? `${PUBLIC_BASE_URL}/api/r/${camp.publicSlug}`
    : null;
  const qrUrl = camp.publicSlug ? api.publicQrUrl(camp.id) : null;

  const provision = async (count = null) => {
    setBusy('provision');
    try {
      const r = await api.provisionGroups(camp.id, count);
      const ok = (r.created || []).filter((x) => x.groupJid).length;
      if (r.reason === 'creation_disabled') notify('Criação de grupos desativada nesta campanha', 'info');
      else if (r.skipped) notify('Nada a provisionar — já há grupos suficientes', 'info');
      else notify(`${ok} grupo(s) criado(s)`, ok > 0 ? 'success' : 'error');
      onChange();
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    } finally {
      setBusy(null);
      setProvisionCount('');
    }
  };

  const refreshSizes = async () => {
    setBusy('refresh');
    try {
      const r = await api.refreshSizes(camp.id);
      notify(`${r.updated} grupos atualizados${r.newlyFull ? `, ${r.newlyFull} ficaram cheios` : ''}`, 'success');
      onChange();
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    } finally {
      setBusy(null);
    }
  };

  const refreshInvites = async () => {
    setBusy('invites');
    try {
      const r = await api.refreshCampaignInvites(camp.id);
      notify(`${r.count || 0} links atualizados${r.errors?.length ? `, ${r.errors.length} com erro` : ''}`, r.errors?.length ? 'info' : 'success');
      onChange();
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    } finally {
      setBusy(null);
    }
  };

  const exportLeads = () => {
    window.open(api.exportLeadsUrl(camp.id), '_blank', 'noopener,noreferrer');
    notify('Exportação de leads iniciada', 'success');
  };

  const copyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    notify('Link copiado!', 'success');
  };

  const unlink = async (g) => {
    if (!window.confirm(`Remover "${g.subject}" desta campanha? O grupo no WhatsApp não será deletado.`)) return;
    try {
      await api.unlinkCampaignGroup(camp.id, g.id);
      notify('Grupo removido da campanha', 'success');
      onChange();
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    }
  };

  const toggleAvailability = async (g, isActive) => {
    const action = isActive ? 'reabrir' : 'fechar';
    if (!isActive && !window.confirm(`Fechar "${g.subject || 'grupo'}" no funil? O link público não enviará mais leads para ele e o sistema tentará manter outro grupo disponível.`)) return;
    setBusy(`availability:${g.id}`);
    try {
      const r = await api.setCampaignGroupAvailability(camp.id, g.id, isActive);
      const created = (r.provision?.created || []).filter((x) => x.groupJid).length;
      if (!isActive && r.provision?.error) {
        notify(`Grupo fechado, mas a reposição automática falhou: ${r.provision.error}`, 'info');
      } else if (!isActive && created > 0) {
        notify(`Grupo fechado e ${created} novo(s) grupo(s) provisionado(s)`, 'success');
      } else {
        notify(`Grupo ${isActive ? 'reaberto' : 'fechado'} no funil`, 'success');
      }
      onChange();
    } catch (e) {
      notify(`Erro ao ${action} grupo: ${e?.response?.data?.detail || e.message}`, 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {/* Public link card */}
      {publicUrl ? (
        <Surface className="p-5 mb-5 border-lime-500/30 bg-gradient-to-br from-lime-500/5 to-transparent">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.15em] text-lime-400 mb-2">
                <Funnel size={12} weight="fill" /> link público do funil
              </div>
              <p className="text-sm text-zinc-400 mb-2">
                Compartilhe este link. O sistema redireciona automaticamente para o próximo grupo disponível.
              </p>
              <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-700 px-3 py-2 rounded-sm">
                <code className="text-lime-300 font-mono text-sm truncate flex-1" data-testid="public-url">
                  {publicUrl}
                </code>
                <button
                  onClick={copyLink}
                  className="text-zinc-400 hover:text-lime-300 p-1"
                  title="Copiar"
                  data-testid="copy-public-url-btn"
                >
                  <Copy size={16} />
                </button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-zinc-400 hover:text-lime-300 p-1"
                  title="Testar"
                  data-testid="open-public-url-btn"
                >
                  <LinkIcon size={16} />
                </a>
              </div>
              <div className="mt-2 text-xs text-zinc-400 font-mono">
                origem por canal: <span className="text-lime-300">{publicUrl}?src=instagram</span>
              </div>
            </div>
            {qrUrl && (
              <div className="w-full sm:w-[168px] border border-zinc-700 bg-zinc-950 p-3">
                <img src={qrUrl} alt="QR Code do link público" className="w-full aspect-square bg-white" />
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Button as="a" href={qrUrl} target="_blank" rel="noreferrer" size="sm" variant="secondary">
                    <QrCode size={14} /> Abrir
                  </Button>
                  <Button as="button" type="button" size="sm" variant="secondary" onClick={() => window.print()}>
                    <Printer size={14} /> Print
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Surface>
      ) : null}

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button onClick={() => setShowSettings(true)} data-testid="open-settings-btn">
          <Gear size={14} weight="bold" />
          Criar grupos
        </Button>
        <Button variant="secondary" onClick={() => setShowBulkEdit(true)} data-testid="bulk-edit-groups-btn">
          <PencilSimple size={14} />
          Alterar grupos
        </Button>
        <Button variant="secondary" onClick={() => setShowImport(true)} data-testid="import-groups-in-campaign">
          <DownloadSimple size={14} weight="bold" />
          Importar grupos
        </Button>
        <Button
          variant="secondary"
          onClick={() => provision(null)}
          disabled={!settings || busy === 'provision'}
          data-testid="provision-auto-btn"
        >
          {busy === 'provision' ? <Spinner /> : <Sparkle size={14} weight="fill" />}
          Provisionar (auto)
        </Button>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            min={1}
            max={50}
            placeholder="N"
            value={provisionCount}
            onChange={(e) => setProvisionCount(e.target.value)}
            className="w-16"
            data-testid="provision-count-input"
          />
          <Button
            variant="secondary"
            onClick={() => provision(Number(provisionCount))}
            disabled={!settings || busy || !provisionCount}
            data-testid="provision-n-btn"
          >
          criar N
        </Button>
      </div>
        <Button
          variant="secondary"
          onClick={refreshInvites}
          disabled={busy === 'invites'}
          data-testid="refresh-invites-btn"
        >
          <LinkIcon size={14} className={busy === 'invites' ? 'animate-spin' : ''} />
          Atualizar links
        </Button>
        <Button
          variant="secondary"
          onClick={exportLeads}
          data-testid="export-leads-btn"
        >
          <DownloadSimple size={14} />
          Exportar leads
        </Button>
        <Button
          variant="secondary"
          onClick={refreshSizes}
          disabled={busy === 'refresh'}
          data-testid="refresh-sizes-btn"
        >
          <ArrowsClockwise size={14} className={busy === 'refresh' ? 'animate-spin' : ''} />
          Atualizar tamanhos
        </Button>
      </div>

      {!settings ? (
        <EmptyState
          icon={Gear}
          title="Configure a campanha primeiro"
          hint="Defina nome padrão, descrição, foto, admins, limite por grupo e quantos grupos manter sempre disponíveis. Em seguida, clique em Provisionar."
          action={<Button onClick={() => setShowSettings(true)} data-testid="empty-configure-btn">Abrir configurações</Button>}
        />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={UsersThree}
          title="Nenhum grupo provisionado ainda"
          hint={`Clique em "Provisionar" para criar ${settings.alwaysAvailable} grupos automaticamente seguindo as configurações.`}
          action={<Button onClick={() => provision(null)} data-testid="empty-provision-btn">Provisionar agora</Button>}
        />
      ) : (
        <Surface className="overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-2.5 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400 border-b border-zinc-700 bg-zinc-950">
            <div className="col-span-1">#</div>
            <div className="col-span-4">Nome</div>
            <div className="col-span-2">Membros / capacidade</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-right">Hits</div>
            <div className="col-span-2 text-right">Ações</div>
          </div>
          <ul className="divide-y divide-zinc-900">
            {groups.map((g) => {
              const size = g.size || 0;
              const limit = g.limit || settings.participantLimit || 200;
              const operational = groupOperationalStatus(g, settings);
              const pct = operational.pct;
              return (
                <li
                  key={g.id}
                  className="grid grid-cols-12 px-4 py-3 items-center text-sm hover:bg-zinc-900/40"
                  data-testid={`campaign-group-${g.id}`}
                >
                  <div className="col-span-1 font-mono text-lime-400 font-bold">
                    {g.counter ? `#${g.counter}` : '—'}
                  </div>
                  <div className="col-span-4 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-sm bg-zinc-900 border border-zinc-700 grid place-items-center text-xs font-mono text-lime-400 shrink-0 overflow-hidden">
                      {g.pictureUrl ? (
                        <img src={g.pictureUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        (g.subject || '?').slice(0, 2).toUpperCase()
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-zinc-100 truncate">{g.subject || 'Sem nome'}</p>
                      <p className="text-xs text-zinc-400 truncate">{g.groupJid ? shortJid(g.groupJid) : '—'}</p>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-zinc-300">
                        {size}/{limit}
                      </span>
                    </div>
                    <div className="h-1 bg-zinc-900 rounded-sm overflow-hidden mt-1">
                      <div
                        className={cn(
                          'h-full transition-all',
                          operational.key === 'closed' ? 'bg-zinc-500' : operational.key === 'full' ? 'bg-red-500' : operational.key === 'near' ? 'bg-amber-500' : 'bg-lime-500'
                        )}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={cn('w-2.5 h-2.5 rounded-full shadow-[0_0_10px_currentColor]', operational.dot)} />
                      <Badge variant={operational.badge}>{operational.label}</Badge>
                    </div>
                    {g.source === 'imported' && (
                      <Badge variant="default" className="ml-1">importado</Badge>
                    )}
                  </div>
                  <div className="col-span-1 text-right font-mono text-xs text-zinc-400">
                    {g.redirectHits || 0}
                    {g.redirectSources && (
                      <div className="text-[10px] text-zinc-500 truncate" title={Object.entries(g.redirectSources).map(([k, v]) => `${k}: ${v}`).join(' · ')}>
                        {Object.entries(g.redirectSources)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 2)
                          .map(([k, v]) => `${k}:${v}`)
                          .join(' ')}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 text-right flex justify-end gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleAvailability(g, g.isActive === false || g.manualClosed)}
                      disabled={busy === `availability:${g.id}`}
                      title={g.isActive === false || g.manualClosed ? 'Reabrir no funil' : 'Fechar no funil'}
                      data-testid={`toggle-group-availability-${g.id}`}
                    >
                      {busy === `availability:${g.id}` ? <Spinner /> : (g.isActive === false || g.manualClosed ? <LockOpen size={14} /> : <Lock size={14} />)}
                    </Button>
                    {g.inviteUrl && (
                      <a
                        href={g.inviteUrl}
                        target="_blank"
                        rel="noreferrer"
                        title="Abrir link de convite"
                        className="text-zinc-400 hover:text-lime-300 p-1"
                        data-testid={`invite-${g.id}`}
                      >
                        <LinkIcon size={14} />
                      </a>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditing(g)}
                      disabled={!g.groupJid}
                      data-testid={`edit-group-${g.id}`}
                    >
                      <PencilSimple size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => unlink(g)}
                      data-testid={`unlink-group-${g.id}`}
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Surface>
      )}

      {showSettings && (
        <CampaignSettingsModal
          camp={camp}
          onClose={() => setShowSettings(false)}
          onDone={() => {
            setShowSettings(false);
            notify('Configurações salvas', 'success');
            onChange();
          }}
          onError={(m) => notify(m, 'error')}
        />
      )}
      {showImport && (
        <ImportGroupsModal
          campaignId={camp.id}
          instance={camp.instanceName}
          existing={new Set(groups.map((g) => g.groupJid).filter(Boolean))}
          onClose={() => setShowImport(false)}
          onDone={(r) => {
            setShowImport(false);
            notify(`${r.count} grupos importados`, 'success');
            onChange();
          }}
          onError={(m) => notify(m, 'error')}
        />
      )}
      {showBulkEdit && (
        <BulkEditGroupsModal
          campaignId={camp.id}
          groups={groups}
          onClose={() => setShowBulkEdit(false)}
          onDone={(r) => {
            setShowBulkEdit(false);
            notify(`${r.count || 0} grupos processados${r.errors?.length ? `, ${r.errors.length} com erro` : ''}`, r.errors?.length ? 'info' : 'success');
            onChange();
          }}
          onError={(m) => notify(m, 'error')}
        />
      )}
      {editing && (
        <EditGroupModal
          group={editing}
          instance={camp.instanceName}
          onClose={() => setEditing(null)}
          onChange={onChange}
          notify={notify}
        />
      )}
    </>
  );
}

/* ---- Campaign Settings Modal (the new core form) ---- */
function CampaignSettingsModal({ camp, onClose, onDone, onError }) {
  const s = camp.groupSettings || {};
  const initialCounterPosition = s.counterPosition || (s.showCounterInName === false ? 'none' : 'suffix');
  const [namePattern, setNamePattern] = useState((s.namePattern || camp.name || '').replace(/\s*#?\{[in]\}\s*/gi, '').trim() || camp.name);
  const [description, setDescription] = useState(s.description || '');
  const [startCounter, setStartCounter] = useState(s.startCounter ?? 1);
  const [alwaysAvailable, setAlwaysAvailable] = useState(s.alwaysAvailable ?? 3);
  const [participantLimit, setParticipantLimit] = useState(s.participantLimit ?? 200);
  const [pictureUrl, setPictureUrl] = useState(s.pictureUrl || '');
  const [picturePreview, setPicturePreview] = useState(s.pictureUrl || '');
  const [adminsRaw, setAdminsRaw] = useState((s.admins || []).join(', '));
  const [onlyAdminsCanSend, setOnlyAdminsCanSend] = useState(s.onlyAdminsCanSend ?? true);
  const [onlyAdminsCanEditSettings, setOnlyAdminsCanEditSettings] = useState(s.onlyAdminsCanEditSettings ?? true);
  const [counterPosition, setCounterPosition] = useState(initialCounterPosition);
  const [disableGroupCreation, setDisableGroupCreation] = useState(s.disableGroupCreation ?? false);
  const [createCommunity, setCreateCommunity] = useState(s.createCommunity ?? false);
  const [publicSlug, setPublicSlug] = useState(camp.publicSlug || '');
  const [busy, setBusy] = useState(false);
  const admins = parsePhoneList(adminsRaw);

  const previewName = (() => {
    const i = Number(startCounter) || 1;
    if (counterPosition === 'prefix') return `#${i} ${namePattern}`;
    if (counterPosition === 'suffix') return `${namePattern} #${i}`;
    return namePattern;
  })();

  const submit = async () => {
    if (!namePattern.trim()) return onError('Defina o nome do grupo');
    if (admins.length === 0) return onError('Adicione ao menos 1 administrador (será o seed da criação)');
    setBusy(true);
    try {
      await api.updateCampaignSettings(camp.id, {
        groupSettings: {
          namePattern: namePattern.trim(),
          description: description.trim() || null,
          startCounter: Number(startCounter) || 1,
          alwaysAvailable: Number(alwaysAvailable) || 3,
          participantLimit: Number(participantLimit) || 200,
          pictureUrl: pictureUrl.trim() || null,
          admins,
          onlyAdminsCanSend,
          showCounterInName: counterPosition !== 'none',
          counterPosition,
          onlyAdminsCanEditSettings,
          disableGroupCreation,
          createCommunity,
        },
        publicSlug: publicSlug.trim() || null,
      });
      onDone();
    } catch (e) {
      onError(`Erro: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const uploadPicture = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return onError('Escolha uma imagem válida');
    if (file.size > 2 * 1024 * 1024) return onError('Use uma imagem com até 2 MB');
    const reader = new FileReader();
    reader.onload = async () => {
      const value = String(reader.result || '');
      setPicturePreview(value);
      setBusy(true);
      try {
        const uploaded = await api.uploadImage({ dataUrl: value, filename: file.name });
        setPictureUrl(uploaded.url);
      } catch (e) {
        onError(`Erro ao subir imagem: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setBusy(false);
      }
    };
    reader.onerror = () => onError('Não consegui ler a imagem');
    reader.readAsDataURL(file);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Configurações da campanha"
      testId="campaign-settings-modal"
      panelClassName="max-w-3xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || !namePattern.trim()} data-testid="save-settings-btn">
            {busy ? <Spinner /> : 'Salvar configurações'}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-[128px_1fr] gap-5 items-start">
          <div className="space-y-3">
            <div className="w-28 h-28 mx-auto rounded-sm border border-zinc-700 bg-zinc-900 grid place-items-center overflow-hidden">
              {picturePreview || pictureUrl ? (
                <img src={picturePreview || pictureUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon size={32} className="text-zinc-500" />
              )}
            </div>
            <label className="w-full inline-flex items-center justify-center gap-2 rounded-sm transition-colors duration-150 h-8 px-3 text-xs bg-zinc-900 hover:bg-zinc-800 text-zinc-100 border border-zinc-700 cursor-pointer">
              <UploadSimple size={14} /> Arquivo
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                className="hidden"
                onChange={(e) => uploadPicture(e.target.files?.[0])}
                data-testid="settings-picture-file"
              />
            </label>
          </div>

          <div className="space-y-5">
            <SectionHeader icon={PencilSimple}>Informações</SectionHeader>
            <div>
              <Label>Nome dos grupos</Label>
              <Input
                value={namePattern}
                onChange={(e) => setNamePattern(e.target.value)}
                placeholder="Lançamento - Priscila Toneto"
                data-testid="settings-name-input"
              />
              <p className="text-xs text-zinc-400 mt-1 font-mono">
                preview: <span className="text-lime-300">{previewName}</span>
              </p>
            </div>

            <div>
              <Label>Numeração no nome</Label>
              <SegmentedControl
                value={counterPosition}
                onChange={setCounterPosition}
                options={[
                  ['none', 'Sem número'],
                  ['prefix', 'Início'],
                  ['suffix', 'Final'],
                ]}
                testId="settings-counter-position"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Início da contagem</Label>
                <Input type="number" min={0} value={startCounter} onChange={(e) => setStartCounter(e.target.value)} data-testid="settings-start-counter" />
              </div>
              <div>
                <Label>Grupos disponíveis</Label>
                <Input type="number" min={0} max={50} value={alwaysAvailable} onChange={(e) => setAlwaysAvailable(e.target.value)} data-testid="settings-always-available" />
              </div>
              <div>
                <Label>Limite de pessoas</Label>
                <Input type="number" min={2} max={1024} value={participantLimit} onChange={(e) => setParticipantLimit(e.target.value)} data-testid="settings-limit" />
              </div>
            </div>

            <div>
              <Label>Descrição do grupo</Label>
              <Textarea rows={5} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Seja bem-vindo(a) ao Grupo VIP..." data-testid="settings-desc-input" />
              <p className="text-xs text-zinc-400 mt-1 font-mono">{description.length}/2048</p>
            </div>

            <div>
              <Label>Nova imagem por URL</Label>
              <Input
                value={pictureUrl}
                onChange={(e) => {
                  setPictureUrl(e.target.value);
                  setPicturePreview(e.target.value);
                }}
                placeholder="https://.../foto.jpg ou data:image/png;base64,..."
                data-testid="settings-picture-input"
              />
            </div>

            <SectionHeader icon={Lock}>Permissões e criação</SectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ToggleRow checked={onlyAdminsCanSend} onChange={setOnlyAdminsCanSend} testId="settings-only-admins">
                Só admins podem enviar mensagens
              </ToggleRow>
              <ToggleRow checked={onlyAdminsCanEditSettings} onChange={setOnlyAdminsCanEditSettings} testId="settings-only-admins-edit">
                Só admins podem alterar configurações
              </ToggleRow>
              <ToggleRow checked={disableGroupCreation} onChange={setDisableGroupCreation} testId="settings-disable-creation">
                Desativar criação de grupos
              </ToggleRow>
              <ToggleRow checked={createCommunity} onChange={setCreateCommunity} testId="settings-create-community">
                Criar comunidade ao invés de grupos
              </ToggleRow>
            </div>

            <SectionHeader icon={Crown}>Administradores</SectionHeader>
            <div>
              <Label>Números dos admins</Label>
              <Textarea rows={3} value={adminsRaw} onChange={(e) => setAdminsRaw(e.target.value)} placeholder="5511999999999, 5521988888888" data-testid="settings-admins-input" />
              <p className="text-xs text-zinc-400 mt-1 font-mono">
                {admins.length} admin(s) - o primeiro é usado como participante inicial na criação
              </p>
            </div>

            <SectionHeader icon={LinkIcon}>Link público</SectionHeader>
            <div>
              <Label>Slug do link</Label>
              <Input value={publicSlug} onChange={(e) => setPublicSlug(e.target.value)} placeholder="lancamento-priscila-toneto" data-testid="settings-slug-input" />
            </div>
          </div>
        </div>

        <div className="text-xs text-zinc-400 font-mono pt-3 border-t border-zinc-700">
          Alterações desta tela valem para os próximos grupos. Para grupos já criados, use Alterar grupos.
        </div>
      </div>
    </Modal>
  );
}

function SegmentedControl({ value, onChange, options, testId }) {
  return (
    <div className="grid grid-cols-3 border border-zinc-700 rounded-sm overflow-hidden" data-testid={testId}>
      {options.map(([id, label]) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          className={cn(
            'h-9 px-3 text-xs font-semibold border-r border-zinc-700 last:border-r-0 transition-colors',
            value === id ? 'bg-lime-500 text-zinc-950' : 'bg-zinc-950 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900'
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function ToggleRow({ checked, onChange, children, testId }) {
  return (
    <label className="flex items-center justify-between gap-3 border border-zinc-700 bg-zinc-950 px-3 py-3 rounded-sm cursor-pointer hover:border-zinc-700">
      <span className="text-sm text-zinc-300">{children}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-lime-500 w-4 h-4 shrink-0"
        data-testid={testId}
      />
    </label>
  );
}

function SectionHeader({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 pt-2 border-t border-zinc-700 -mx-1 px-1">
      <Icon size={14} className="text-lime-500" />
      <h4 className="text-xs font-mono uppercase tracking-[0.12em] text-zinc-300">{children}</h4>
    </div>
  );
}
/* ---- Import existing WhatsApp groups ---- */
function ImportGroupsModal({ campaignId, instance, existing, onClose, onDone, onError }) {
  const [allGroups, setAllGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const list = await api.listGroups(instance, refresh, false);
      setAllGroups(list);
    } catch (e) {
      onError(`Erro: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(false);
    // eslint-disable-next-line
  }, []);

  const filtered = allGroups.filter((g) =>
    !search.trim() || (g.subject || '').toLowerCase().includes(search.toLowerCase())
  );
  const available = filtered.filter((g) => !existing.has(g.groupJid));

  const toggle = (jid) => {
    setSelected((s) => {
      const ns = new Set(s);
      if (ns.has(jid)) ns.delete(jid);
      else ns.add(jid);
      return ns;
    });
  };

  const toggleAll = () => {
    if (selected.size === available.length) setSelected(new Set());
    else setSelected(new Set(available.map((g) => g.groupJid)));
  };

  const submit = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const r = await api.importGroupsToCampaign(campaignId, { groupJids: Array.from(selected) });
      onDone(r);
    } catch (e) {
      onError(`Erro: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Importar grupos existentes"
      testId="import-groups-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="secondary" onClick={() => load(true)} disabled={loading} data-testid="resync-import-btn">
            <ArrowsClockwise size={12} className={loading ? 'animate-spin' : ''} />
            Sincronizar do WhatsApp
          </Button>
          <Button onClick={submit} disabled={busy || selected.size === 0} data-testid="confirm-import-btn">
            {busy ? <Spinner /> : `Importar ${selected.size} grupos`}
          </Button>
        </>
      }
    >
      <Input
        placeholder="Buscar grupo…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-3"
        data-testid="import-search"
      />
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-mono text-zinc-400">
          {selected.size} / {available.length} selecionados ({allGroups.length} no total, {existing.size} já na campanha)
        </p>
        <button
          onClick={toggleAll}
          className="text-xs font-mono text-lime-400 hover:text-lime-300"
          data-testid="import-toggle-all"
        >
          {selected.size === available.length ? 'desmarcar' : 'todos'}
        </button>
      </div>
      <div className="max-h-[400px] overflow-y-auto border border-zinc-700 rounded-sm divide-y divide-zinc-900">
        {loading ? (
          <div className="p-4 text-zinc-400 flex items-center gap-2"><Spinner /> carregando…</div>
        ) : available.length === 0 ? (
          <div className="p-4 text-zinc-500 text-sm font-mono">
            Nenhum grupo disponível para importar. Clique em &quot;Sincronizar do WhatsApp&quot;.
          </div>
        ) : (
          available.map((g) => {
            const checked = selected.has(g.groupJid);
            return (
              <label
                key={g.groupJid}
                className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-900/40 cursor-pointer"
                data-testid={`import-row-${shortJid(g.groupJid)}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(g.groupJid)}
                  className="accent-lime-500"
                />
                <span className="text-sm text-zinc-200 truncate flex-1">{g.subject || 'Sem nome'}</span>
                <span className="text-xs font-mono text-zinc-400">{g.size || 0}</span>
              </label>
            );
          })
        )}
      </div>
    </Modal>
  );
}

function BulkEditGroupsModal({ campaignId, groups, onClose, onDone, onError }) {
  const eligible = (groups || []).filter((g) => g.groupJid);
  const [subject, setSubject] = useState('');
  const [counterPosition, setCounterPosition] = useState('suffix');
  const [description, setDescription] = useState('');
  const [pictureUrl, setPictureUrl] = useState('');
  const [picturePreview, setPicturePreview] = useState('');
  const [onlyAdminsCanSend, setOnlyAdminsCanSend] = useState(null);
  const [onlyAdminsCanEditSettings, setOnlyAdminsCanEditSettings] = useState(null);
  const [settingAction, setSettingAction] = useState('');
  const [participantsAction, setParticipantsAction] = useState('');
  const [participantsRaw, setParticipantsRaw] = useState('');
  const [delaySeconds, setDelaySeconds] = useState(1);
  const [busy, setBusy] = useState(false);

  const uploadPicture = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return onError('Escolha uma imagem válida');
    if (file.size > 2 * 1024 * 1024) return onError('Use uma imagem com até 2 MB');
    const reader = new FileReader();
    reader.onload = async () => {
      const value = String(reader.result || '');
      setPicturePreview(value);
      setBusy(true);
      try {
        const uploaded = await api.uploadImage({ dataUrl: value, filename: file.name });
        setPictureUrl(uploaded.url);
      } catch (e) {
        onError(`Erro ao subir imagem: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setBusy(false);
      }
    };
    reader.onerror = () => onError('Não consegui ler a imagem');
    reader.readAsDataURL(file);
  };

  const renderSubjectPattern = () => {
    const clean = subject.trim();
    if (!clean) return '';
    if (counterPosition === 'prefix') return '#{i} ' + clean;
    if (counterPosition === 'suffix') return clean + ' #{i}';
    return clean;
  };

  const resolveSettingActions = () => {
    const actions = [];
    if (settingAction) actions.push(settingAction);
    if (onlyAdminsCanSend === true) actions.push('announcement');
    if (onlyAdminsCanSend === false) actions.push('not_announcement');
    if (onlyAdminsCanEditSettings === true) actions.push('locked');
    if (onlyAdminsCanEditSettings === false) actions.push('unlocked');
    return Array.from(new Set(actions));
  };

  const submit = async () => {
    if (eligible.length === 0) return onError('Não há grupos reais para alterar');
    const participants = parsePhoneList(participantsRaw);
    if (participantsAction && participants.length === 0) return onError('Informe números para alterar participantes/admins');
    const finalSettingActions = resolveSettingActions();
    if (!subject.trim() && !description.trim() && !pictureUrl.trim() && finalSettingActions.length === 0 && !participantsAction) {
      return onError('Escolha ao menos uma alteração');
    }
    setBusy(true);
    try {
      const r = await api.bulkUpdateCampaignGroups(campaignId, {
        subject: renderSubjectPattern() || undefined,
        description: description.trim() || undefined,
        pictureUrl: pictureUrl.trim() || undefined,
        settingActions: finalSettingActions,
        participantsAction: participantsAction || undefined,
        participants,
        delaySeconds: Number(delaySeconds) || 0,
      });
      onDone(r);
    } catch (e) {
      onError(`Erro: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Alterar grupos em massa"
      testId="bulk-edit-groups-modal"
      panelClassName="max-w-3xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || eligible.length === 0} data-testid="bulk-edit-submit">
            {busy ? <Spinner /> : `Aplicar em ${eligible.length} grupos`}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <Surface className="p-4 border-lime-500/20 bg-lime-500/5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-zinc-100">Aplicar em todos os grupos da campanha</p>
              <p className="text-xs text-zinc-400 mt-1">
                {eligible.length} grupo(s) reais serão alterados em sequência, respeitando o delay entre grupos.
              </p>
            </div>
            <Badge variant="success">{eligible.length} grupos</Badge>
          </div>
        </Surface>

        <div className="grid grid-cols-1 lg:grid-cols-[128px_1fr] gap-5 items-start">
          <div className="space-y-3">
            <div className="w-28 h-28 mx-auto rounded-sm border border-zinc-700 bg-zinc-900 grid place-items-center overflow-hidden">
              {picturePreview || pictureUrl ? (
                <img src={picturePreview || pictureUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon size={32} className="text-zinc-500" />
              )}
            </div>
            <label className="w-full inline-flex items-center justify-center gap-2 rounded-sm transition-colors duration-150 h-8 px-3 text-xs bg-zinc-900 hover:bg-zinc-800 text-zinc-100 border border-zinc-700 cursor-pointer">
              <UploadSimple size={14} /> Arquivo
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                className="hidden"
                onChange={(e) => uploadPicture(e.target.files?.[0])}
                data-testid="bulk-picture-file"
              />
            </label>
          </div>

          <div className="space-y-5">
            <SectionHeader icon={PencilSimple}>Informações</SectionHeader>
            <div>
              <Label>Novo nome dos grupos</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Lançamento - Priscila Toneto" data-testid="bulk-subject" />
              <p className="text-xs text-zinc-400 mt-1 font-mono">
                preview: <span className="text-lime-300">{renderSubjectPattern().replace('{i}', eligible[0]?.counter || 1) || 'sem alteração'}</span>
              </p>
            </div>

            <div>
              <Label>Numeração no nome</Label>
              <SegmentedControl
                value={counterPosition}
                onChange={setCounterPosition}
                options={[
                  ['none', 'Sem número'],
                  ['prefix', 'Início'],
                  ['suffix', 'Final'],
                ]}
                testId="bulk-counter-position"
              />
            </div>

            <div>
              <Label>Nova descrição</Label>
              <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="bulk-description" />
            </div>

            <div>
              <Label>Nova imagem por URL</Label>
              <Input
                value={pictureUrl}
                onChange={(e) => {
                  setPictureUrl(e.target.value);
                  setPicturePreview(e.target.value);
                }}
                placeholder="https://.../foto.jpg ou data:image/png;base64,..."
                data-testid="bulk-picture"
              />
            </div>

            <SectionHeader icon={Lock}>Permissões</SectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <TriStateToggle value={onlyAdminsCanSend} onChange={setOnlyAdminsCanSend} testId="bulk-only-admins">
                Só admins podem enviar mensagens
              </TriStateToggle>
              <TriStateToggle value={onlyAdminsCanEditSettings} onChange={setOnlyAdminsCanEditSettings} testId="bulk-only-admins-edit">
                Só admins podem alterar configurações
              </TriStateToggle>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Configuração avançada</Label>
                <select value={settingAction} onChange={(e) => setSettingAction(e.target.value)} className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm px-3 py-2 rounded-sm font-mono" data-testid="bulk-setting">
                  <option value="">Usar flags acima</option>
                  <option value="announcement">Fechar: só admins falam</option>
                  <option value="not_announcement">Abrir: todos falam</option>
                  <option value="locked">Só admins editam infos</option>
                  <option value="unlocked">Todos editam infos</option>
                </select>
              </div>
              <div>
                <Label>Delay entre grupos</Label>
                <Input type="number" min={0} max={30} value={delaySeconds} onChange={(e) => setDelaySeconds(e.target.value)} />
              </div>
            </div>

            <SectionHeader icon={Users}>Participantes / admins</SectionHeader>
            <div>
              <select value={participantsAction} onChange={(e) => setParticipantsAction(e.target.value)} className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm px-3 py-2 rounded-sm font-mono mb-2" data-testid="bulk-participants-action">
                <option value="">Não alterar</option>
                <option value="add">Adicionar participantes</option>
                <option value="remove">Remover participantes</option>
                <option value="promote">Adicionar administradores</option>
                <option value="demote">Remover administradores</option>
              </select>
              <Textarea rows={3} value={participantsRaw} onChange={(e) => setParticipantsRaw(e.target.value)} placeholder="5511999999999, um por linha ou separado por vírgula" data-testid="bulk-participants" />
            </div>
          </div>
        </div>

        <div className="text-xs text-zinc-400 font-mono pt-3 border-t border-zinc-700">
          Alterações serão aplicadas uma por vez em todos os grupos reais desta campanha.
        </div>
      </div>
    </Modal>
  );
}

function TriStateToggle({ value, onChange, children, testId }) {
  const label = value === null ? 'Não alterar' : value ? 'Sim' : 'Não';
  const next = () => onChange(value === null ? true : value === true ? false : null);
  return (
    <button
      type="button"
      onClick={next}
      data-testid={testId}
      className="flex items-center justify-between gap-3 border border-zinc-700 bg-zinc-950 px-3 py-3 rounded-sm hover:border-zinc-700 text-left"
    >
      <span className="text-sm text-zinc-300">{children}</span>
      <span className={cn(
        'text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-1 border rounded-sm shrink-0',
        value === null ? 'text-zinc-400 border-zinc-700 bg-zinc-900' : value ? 'text-lime-300 border-lime-500/40 bg-lime-500/10' : 'text-red-300 border-red-500/40 bg-red-500/10'
      )}>
        {label}
      </span>
    </button>
  );
}

/* ---- Edit existing group (subject/desc/picture/setting/admins/invite/leave) ---- */
function EditGroupModal({ group, instance, onClose, onChange, notify }) {
  const [subject, setSubject] = useState(group.subject || '');
  const [description, setDescription] = useState(group.description || '');
  const [pictureUrl, setPictureUrl] = useState('');
  const [participantsRaw, setParticipantsRaw] = useState('');
  const [busy, setBusy] = useState(null);
  const [invite, setInvite] = useState(null);

  const wrap = async (key, fn, ok = 'Aplicado') => {
    setBusy(key);
    try {
      await fn();
      notify(ok, 'success');
      onChange?.();
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    } finally {
      setBusy(null);
    }
  };

  const doSubject = () =>
    wrap('subject', () => api.updateSubject({ instanceName: instance, groupJid: group.groupJid, subject }), 'Nome atualizado');
  const doDescription = () =>
    wrap('desc', () => api.updateDescription({ instanceName: instance, groupJid: group.groupJid, description }), 'Descrição atualizada');
  const doPicture = () =>
    wrap('pic', () => api.updatePicture({ instanceName: instance, groupJid: group.groupJid, image: pictureUrl }), 'Foto atualizada');
  const doSetting = (action, msg) =>
    wrap('setting', () => api.updateSetting({ instanceName: instance, groupJid: group.groupJid, action }), msg);
  const doParticipants = (action, msg) => {
    const list = parsePhoneList(participantsRaw);
    if (list.length === 0) return notify('Adicione números primeiro', 'error');
    return wrap('part', () =>
      api.updateParticipants({ instanceName: instance, groupJid: group.groupJid, action, participants: list }), msg);
  };
  const fetchInvite = async () => {
    setBusy('invite');
    try {
      const r = await api.inviteCode(instance, group.groupJid);
      setInvite(r);
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    } finally {
      setBusy(null);
    }
  };
  const revokeInvite = () =>
    wrap('invite', async () => {
      await api.revokeInvite({ instanceName: instance, groupJid: group.groupJid });
      setInvite(null);
    }, 'Link revogado');

  return (
    <Modal
      open
      onClose={onClose}
      title={group.subject || 'Gerenciar grupo'}
      testId="edit-campaign-group-modal"
      footer={<Button variant="ghost" onClick={onClose}>Fechar</Button>}
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400 mb-4">
        JID: {group.groupJid}
      </div>

      <Section title="Nome" icon={PencilSimple}>
        <div className="flex gap-2">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="es-subject" />
          <Button size="sm" onClick={doSubject} disabled={busy === 'subject'} data-testid="es-subject-save">Salvar</Button>
        </div>
      </Section>

      <Section title="Descrição" icon={PencilSimple}>
        <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="es-desc" />
        <Button size="sm" onClick={doDescription} disabled={busy === 'desc'} className="mt-2" data-testid="es-desc-save">
          Salvar descrição
        </Button>
      </Section>

      <Section title="Foto" icon={ImageIcon}>
        <Input value={pictureUrl} onChange={(e) => setPictureUrl(e.target.value)} placeholder="https://… ou data:image/png;base64,…" data-testid="es-pic" />
        <Button size="sm" onClick={doPicture} disabled={busy === 'pic' || !pictureUrl} className="mt-2" data-testid="es-pic-save">
          Atualizar foto
        </Button>
      </Section>

      <Section title="Abrir / Fechar grupo" icon={Lock}>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="secondary" onClick={() => doSetting('not_announcement', 'Grupo aberto')} data-testid="es-open">
            <LockOpen size={14} /> Abrir
          </Button>
          <Button size="sm" variant="secondary" onClick={() => doSetting('announcement', 'Grupo fechado')} data-testid="es-close">
            <Lock size={14} /> Fechar (só admins falam)
          </Button>
          <Button size="sm" variant="secondary" onClick={() => doSetting('locked', 'Edição restrita')}>
            Só admins editam infos
          </Button>
          <Button size="sm" variant="secondary" onClick={() => doSetting('unlocked', 'Edição liberada')}>
            Todos editam infos
          </Button>
        </div>
      </Section>

      <Section title="Participantes & Admins" icon={UserPlus}>
        <Label>Números</Label>
        <Textarea
          rows={2}
          value={participantsRaw}
          onChange={(e) => setParticipantsRaw(e.target.value)}
          placeholder="5511999999999"
          data-testid="es-participants"
        />
        <div className="flex flex-wrap gap-2 mt-2">
          <Button size="sm" onClick={() => doParticipants('add', 'Adicionados')} data-testid="es-add">
            <UserPlus size={14} /> Adicionar
          </Button>
          <Button size="sm" variant="secondary" onClick={() => doParticipants('remove', 'Removidos')} data-testid="es-remove">
            <UserMinus size={14} /> Remover
          </Button>
          <Button size="sm" variant="secondary" onClick={() => doParticipants('promote', 'Promovidos')} data-testid="es-promote">
            <Crown size={14} /> Promover
          </Button>
          <Button size="sm" variant="secondary" onClick={() => doParticipants('demote', 'Rebaixados')} data-testid="es-demote">
            <Crown size={14} /> Rebaixar
          </Button>
        </div>
      </Section>

      <Section title="Link de convite" icon={LinkIcon}>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={fetchInvite} disabled={busy === 'invite'} data-testid="es-invite-get">
            Obter link
          </Button>
          <Button size="sm" variant="danger" onClick={revokeInvite} disabled={busy === 'invite'} data-testid="es-invite-revoke">
            Revogar
          </Button>
        </div>
        {invite?.inviteUrl && (
          <div className="mt-2 p-2 bg-zinc-900 border border-zinc-700 text-xs font-mono text-lime-400 break-all">
            {invite.inviteUrl}
          </div>
        )}
      </Section>
    </Modal>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="border-t border-zinc-700 pt-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} className="text-lime-500" />
        <h4 className="text-xs font-mono uppercase tracking-[0.12em] text-zinc-300">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function sequenceItemTitle(item) {
  if (item.type === 'text') return 'Texto';
  if (item.type === 'audio') return 'Áudio';
  return item.mediatype === 'video' ? 'Vídeo' : item.mediatype === 'document' ? 'Arquivo' : 'Imagem';
}

function sequenceItemSummary(item) {
  const prefix = item.mentionAll ? '@todos · ' : '';
  return `${prefix}${item.text || item.caption || item.fileName || item.media || item.mediaPreview || ''}`.slice(0, 120);
}

function SequenceItemPreview({ item }) {
  const media = item.media || item.mediaPreview || '';
  if (item.type === 'text') {
    return (
      <div className="mt-2 border border-zinc-700 bg-zinc-950 px-3 py-2 rounded-sm">
        {item.mentionAll && <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.12em] text-lime-300">@todos marcado</p>}
        <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words">{item.text}</p>
      </div>
    );
  }
  if (item.type === 'audio') {
    return (
      <div className="mt-2">
        <audio controls src={media} className="w-full h-9" />
      </div>
    );
  }
  if (item.mediatype === 'video') {
    return (
      <div className="mt-2">
        <video controls src={media} className="w-full max-h-48 bg-black border border-zinc-700 rounded-sm" />
      </div>
    );
  }
  if (item.mediatype === 'document') {
    return (
      <div className="mt-2 border border-zinc-700 bg-zinc-950 px-3 py-2 rounded-sm">
        <p className="text-xs font-mono text-zinc-300 truncate">{item.fileName || media || 'arquivo'}</p>
        {media.startsWith('http') && (
          <a href={media} target="_blank" rel="noreferrer" className="text-xs font-mono text-lime-400 hover:text-lime-300">
            abrir arquivo
          </a>
        )}
      </div>
    );
  }
  return (
    <div className="mt-2">
      <img src={media} alt="" className="w-full max-h-48 object-contain bg-zinc-950 border border-zinc-700 rounded-sm" />
    </div>
  );
}

function SequenceList({ items, onEdit, onRemove, testId }) {
  if (!items.length) return null;
  return (
    <div className="mb-5 border border-zinc-700 bg-zinc-950 rounded-sm overflow-hidden" data-testid={testId}>
      <div className="px-3 py-2 border-b border-zinc-700 flex items-center justify-between">
        <span className="text-xs font-mono uppercase tracking-[0.12em] text-zinc-400">Sequência</span>
        <Badge variant="success">{items.length} mensagens</Badge>
      </div>
      <div className="divide-y divide-zinc-900">
        {items.map((item, index) => (
          <div key={`${item.type}-${item.mediatype || 'text'}-${index}`} className="px-3 py-3">
            <div className="flex items-start gap-3">
              <span className="w-7 h-7 bg-lime-500 text-zinc-950 font-mono text-xs font-bold grid place-items-center rounded-sm shrink-0">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-zinc-200">{sequenceItemTitle(item)}</p>
                <p className="text-xs text-zinc-400 truncate">{sequenceItemSummary(item) || 'sem prévia'}</p>
                <SequenceItemPreview item={item} />
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => onEdit(index)} data-testid={`edit-sequence-item-${index}`}>
                  <PencilSimple size={14} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onRemove(index)} data-testid={`remove-sequence-item-${index}`}>
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ========================== MESSAGES TAB ========================== */
function MessagesTab({ camp, onChange, notify, setTab }) {
  const groups = (camp.groups || []).filter((g) => !!g.groupJid);
  const [targetMode, setTargetMode] = useState('groups');
  const [broadcastPreview, setBroadcastPreview] = useState(null);
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [excludeAdmins, setExcludeAdmins] = useState(true);
  const [manualRecipientsRaw, setManualRecipientsRaw] = useState('');
  const [manualFileName, setManualFileName] = useState('');
  const [selectedJids, setSelectedJids] = useState(new Set());
  const [items, setItems] = useState([]);
  const [type, setType] = useState('text');
  const [text, setText] = useState('');
  const [mentionAll, setMentionAll] = useState(false);
  const [media, setMedia] = useState('');
  const [caption, setCaption] = useState('');
  const [fileName, setFileName] = useState('');
  const [mimetype, setMimetype] = useState('');
  const [scheduleNow, setScheduleNow] = useState(true);
  const [scheduledAt, setScheduledAt] = useState('');
  const [minDelay, setMinDelay] = useState(3);
  const [maxDelay, setMaxDelay] = useState(8);
  const [busy, setBusy] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const toggle = (jid) => {
    setSelectedJids((s) => {
      const ns = new Set(s);
      if (ns.has(jid)) ns.delete(jid);
      else ns.add(jid);
      return ns;
    });
  };
  const toggleAll = () => {
    if (selectedJids.size === groups.length) setSelectedJids(new Set());
    else setSelectedJids(new Set(groups.map((g) => g.groupJid)));
  };

  const loadBroadcastTargets = async () => {
    setBroadcastLoading(true);
    try {
      const data = await api.broadcastTargets(camp.id, excludeAdmins);
      setBroadcastPreview(data);
      notify(`${data.count || 0} leads encontrados para broadcast`, data.count ? 'success' : 'info');
      return data;
    } catch (e) {
      notify(`Erro ao buscar leads: ${apiErrorMessage(e)}`, 'error');
      return null;
    } finally {
      setBroadcastLoading(false);
    }
  };

  const manualRecipients = Array.from(new Set(parsePhoneList(manualRecipientsRaw)));
  const loadManualRecipientsFile = (file) => {
    if (!file) return;
    const lower = (file.name || '').toLowerCase();
    if (!lower.endsWith('.csv') && !lower.endsWith('.txt')) {
      return notify('Suba um arquivo CSV ou TXT com os telefones', 'error');
    }
    const reader = new FileReader();
    reader.onload = () => {
      const content = String(reader.result || '');
      setManualRecipientsRaw((current) => [current, content].filter(Boolean).join('\n'));
      setManualFileName(file.name || '');
      notify('Lista carregada. Confira a quantidade antes de disparar.', 'success');
    };
    reader.onerror = () => notify('Não consegui ler a lista de telefones', 'error');
    reader.readAsText(file);
  };

  const mediaKind = type === 'image' || type === 'video' || type === 'document' ? type : null;
  const acceptByType = {
    image: '.jpg,.jpeg,.png,image/jpeg,image/png',
    video: '.mp4,video/mp4',
    audio: '.ogg,audio/ogg,audio/oga',
    document: '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,application/pdf,text/plain',
  };

  const loadFile = async (file, kind = type) => {
    if (!file) return;
    const maxVideo = 100 * 1024 * 1024;
    const maxGeneric = 25 * 1024 * 1024;
    const name = file.name || '';
    const lower = name.toLowerCase();
    if (kind === 'image' && !/\.(jpe?g|png)$/.test(lower)) return notify('Imagem precisa ser JPG, JPEG ou PNG', 'error');
    if (kind === 'video' && (!lower.endsWith('.mp4') || file.size > maxVideo)) return notify('Vídeo precisa ser MP4 com até 100MB', 'error');
    if (kind === 'audio' && !lower.endsWith('.ogg')) return notify('Áudio precisa estar em OGG para melhor compatibilidade no WhatsApp', 'error');
    if (kind !== 'video' && file.size > maxGeneric) return notify('Arquivo precisa ter até 25MB', 'error');
    setType(kind);
    setFileName(name);
    setMimetype(file.type || (kind === 'audio' ? 'audio/ogg' : 'application/octet-stream'));
    setMedia('');
    setUploadingMedia(true);
    notify('Subindo arquivo...', 'info');
    try {
      const uploaded = await api.uploadMedia(file);
      setMedia(uploaded.url);
      setFileName(uploaded.filename || name);
      setMimetype(uploaded.contentType || file.type || (kind === 'audio' ? 'audio/ogg' : 'application/octet-stream'));
      notify('Arquivo carregado no compositor', 'success');
    } catch (e) {
      notify(`Erro ao subir arquivo: ${apiErrorMessage(e)}`, 'error');
      setFileName('');
      setMimetype('');
    } finally {
      setUploadingMedia(false);
    }
  };

  const clearDraft = () => {
    setText('');
    setMentionAll(false);
    setMedia('');
    setCaption('');
    setFileName('');
    setMimetype('');
    setEditingIndex(null);
    setUploadingMedia(false);
  };

  const loadItemToDraft = (item) => {
    if (item.type === 'text') {
      setType('text');
      setText(item.text || '');
      setMentionAll(Boolean(item.mentionAll));
      setMedia('');
      setCaption('');
      setFileName('');
      setMimetype('');
      return;
    }
    if (item.type === 'audio') {
      setType('audio');
      setText('');
      setMentionAll(false);
      setMedia(item.media || '');
      setCaption('');
      setFileName(item.fileName || '');
      setMimetype(item.mimetype || 'audio/ogg');
      return;
    }
    setType(item.mediatype || 'image');
    setText('');
    setMentionAll(false);
    setMedia(item.media || '');
    setCaption(item.caption || item.text || '');
    setFileName(item.fileName || '');
    setMimetype(item.mimetype || '');
  };

  const editItem = (index) => {
    loadItemToDraft(items[index]);
    setEditingIndex(index);
    notify('Item carregado para edição', 'info');
  };

  const buildDraftItem = () => {
    if (type === 'text') {
      if (!text.trim()) return null;
      return { type: 'text', text: text.trim(), mentionAll };
    }
    if (!media.trim()) return null;
    return {
      type: type === 'audio' ? 'audio' : 'media',
      text: type === 'audio' ? undefined : caption || undefined,
      mediatype: mediaKind || undefined,
      media: media.trim(),
      caption: mediaKind ? caption || undefined : undefined,
      fileName: fileName || undefined,
      mimetype: mimetype || undefined,
    };
  };

  const addDraftToSequence = () => {
    if (uploadingMedia) return notify('Aguarde o upload terminar antes de adicionar', 'info');
    const item = buildDraftItem();
    if (!item) return notify(type === 'text' ? 'Escreva o texto' : 'Informe a mídia', 'error');
    if (editingIndex === null && items.length >= 10) return notify('Use no máximo 10 mensagens por sequência', 'error');
    setItems((prev) => {
      if (editingIndex === null) return [...prev, item];
      return prev.map((old, index) => (index === editingIndex ? item : old));
    });
    clearDraft();
    setType('text');
    notify(editingIndex === null ? 'Mensagem adicionada à sequência' : 'Mensagem atualizada na sequência', 'success');
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    if (editingIndex === index) clearDraft();
    else if (editingIndex !== null && index < editingIndex) setEditingIndex((value) => value - 1);
  };

  const draftItem = buildDraftItem();
  const sequenceItems = draftItem && editingIndex === null ? [...items, draftItem] : items;

  const submit = async () => {
    if (targetMode === 'groups' && selectedJids.size === 0) return notify('Selecione ao menos um grupo', 'error');
    if (uploadingMedia) return notify('Aguarde o upload da mídia terminar', 'info');
    if (sequenceItems.length === 0) return notify('Adicione ao menos um texto ou mídia à sequência', 'error');
    if (!scheduleNow && !scheduledAt) return notify('Defina a data/hora', 'error');

    let targets = groups
      .filter((g) => selectedJids.has(g.groupJid))
      .map((g) => ({ groupJid: g.groupJid, subject: g.subject }));

    if (targetMode === 'broadcast') {
      const preview = broadcastPreview || await loadBroadcastTargets();
      if (!preview?.targets?.length) return notify('Nenhum lead encontrado para broadcast', 'error');
      targets = preview.targets.map((target) => ({
        groupJid: target.groupJid || target.jid,
        subject: target.subject || target.jid,
      }));
    }

    if (targetMode === 'manual') {
      if (manualRecipients.length === 0) return notify('Carregue ou cole ao menos um telefone', 'error');
      targets = manualRecipients.map((phone) => ({
        groupJid: `${phone}@s.whatsapp.net`,
        subject: phone,
      }));
      if (Number(minDelay) < 30) {
        return notify('Para broadcast 1x1 use delay mínimo de pelo menos 30 segundos', 'error');
      }
    }

    const body = {
      instanceName: camp.instanceName,
      campaignId: camp.id,
      targets,
      type: sequenceItems[0].type,
      text: sequenceItems[0].text,
      mentionAll: sequenceItems[0].mentionAll,
      mediatype: sequenceItems[0].mediatype,
      media: sequenceItems[0].media,
      caption: sequenceItems[0].caption,
      fileName: sequenceItems[0].fileName,
      mimetype: sequenceItems[0].mimetype,
      items: sequenceItems,
      minDelaySeconds: Number(minDelay),
      maxDelaySeconds: Number(maxDelay),
    };

    setBusy(true);
    try {
      if (scheduleNow) {
        const r = await api.sendNow(body);
        const ok = (r.results || []).filter((x) => x.ok).length;
        notify(`Disparo: ${ok}/${r.results.length} itens enviados`, 'success');
      } else {
        await api.schedule({ ...body, scheduledAt: new Date(scheduledAt).toISOString() });
        notify('Agendado!', 'success');
      }
      setItems([]);
      clearDraft();
      onChange();
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (groups.length === 0) {
    return <EmptyState icon={PaperPlaneTilt} title="Crie ou importe grupos primeiro" hint="Volte à aba Grupos para começar." />;
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
      <Surface className="xl:col-span-3 p-5">
        <h3 className="font-display font-bold text-lg text-zinc-50 mb-4">Nova mensagem</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-5">
          <button
            type="button"
            onClick={() => setTargetMode('groups')}
            className={cn(
              'px-3 py-2 rounded-sm border text-sm transition-colors',
              targetMode === 'groups' ? 'border-lime-500 bg-lime-500/10 text-lime-300' : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:text-zinc-100'
            )}
          >
            Enviar nos grupos
          </button>
          <button
            type="button"
            onClick={() => setTargetMode('broadcast')}
            className={cn(
              'px-3 py-2 rounded-sm border text-sm transition-colors',
              targetMode === 'broadcast' ? 'border-lime-500 bg-lime-500/10 text-lime-300' : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:text-zinc-100'
            )}
          >
            Leads dos grupos
          </button>
          <button
            type="button"
            onClick={() => {
              setTargetMode('manual');
              if (Number(minDelay) < 60) setMinDelay(60);
              if (Number(maxDelay) < 90) setMaxDelay(90);
            }}
            className={cn(
              'px-3 py-2 rounded-sm border text-sm transition-colors',
              targetMode === 'manual' ? 'border-lime-500 bg-lime-500/10 text-lime-300' : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:text-zinc-100'
            )}
          >
            Broadcast lista
          </button>
        </div>

        <SequenceList items={items} onEdit={editItem} onRemove={removeItem} testId="message-sequence-list" />

        <div className="flex gap-2 mb-5">
          {[
            { id: 'text', label: 'Texto' },
            { id: 'image', label: 'Imagem' },
            { id: 'video', label: 'Vídeo' },
            { id: 'audio', label: 'Áudio' },
            { id: 'document', label: 'Arquivo' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setType(t.id);
                setMedia('');
                setFileName('');
                setMimetype('');
              }}
              data-testid={`mt-${t.id}`}
              className={cn(
                'flex-1 px-3 py-2 rounded-sm border text-sm transition-colors',
                type === t.id ? 'border-lime-500 bg-lime-500/10 text-lime-300' : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:text-zinc-100'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {type === 'text' && (
          <div className="space-y-3">
            <Label>Texto</Label>
            <Textarea
              rows={5}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Sua mensagem aqui — use *negrito*, _itálico_, ~tachado~"
              data-testid="mt-text-input"
            />
            <label className="flex items-start gap-3 rounded-sm border border-zinc-800 bg-zinc-950/70 px-3 py-2 text-sm text-zinc-300">
              <input
                type="checkbox"
                checked={mentionAll}
                onChange={(e) => setMentionAll(e.target.checked)}
                className="mt-1 h-4 w-4 accent-lime-500"
                data-testid="mt-mention-all"
              />
              <span>
                <span className="block font-semibold text-zinc-100">Mencionar todos no grupo</span>
                <span className="block text-xs text-zinc-500">Envia o texto marcando todos os participantes. Ignorado em broadcast 1x1.</span>
              </span>
            </label>
          </div>
        )}
        {type !== 'text' && (
          <div className="space-y-3">
            <div>
              <Label>Arquivo</Label>
              <label className="flex items-center justify-center gap-2 border border-dashed border-zinc-700 bg-zinc-950 hover:border-lime-500/60 cursor-pointer rounded-sm px-3 py-5 text-sm text-zinc-400 transition-colors">
                <UploadSimple size={16} />
                <span>{uploadingMedia ? 'Subindo arquivo...' : fileName || 'Escolher arquivo'}</span>
                <input
                  type="file"
                  accept={acceptByType[type]}
                  onChange={(e) => {
                    const input = e.currentTarget;
                    loadFile(input.files?.[0], type).finally(() => {
                      input.value = '';
                    });
                  }}
                  className="hidden"
                  data-testid="mt-file-input"
                />
              </label>
            </div>
            <div>
              <Label>URL ou base64</Label>
              <Textarea
                rows={2}
                value={media}
                onChange={(e) => setMedia(e.target.value)}
                placeholder={type === 'audio' ? 'Cole URL/base64 de áudio OGG' : 'Cole URL/base64 ou use o seletor acima'}
                data-testid="mt-media-input"
              />
            </div>
            {type !== 'audio' && (
              <div>
                <Label>Texto / legenda (opcional)</Label>
                <Textarea rows={3} value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="mt-caption-input" />
              </div>
            )}
            {type === 'document' && (
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="arquivo.pdf" value={fileName} onChange={(e) => setFileName(e.target.value)} />
                <Input placeholder="application/pdf" value={mimetype} onChange={(e) => setMimetype(e.target.value)} />
              </div>
            )}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="text-xs text-zinc-400">
            {editingIndex !== null
              ? `Editando o item ${editingIndex + 1}. Salve para atualizar a sequência.`
              : items.length > 0
              ? 'O rascunho atual também será enviado no final se tiver conteúdo.'
              : 'Para enviar texto + mídia + texto, adicione cada parte à sequência.'}
          </p>
          <Button variant="secondary" onClick={addDraftToSequence} disabled={uploadingMedia || !draftItem || (editingIndex === null && items.length >= 10)} data-testid="add-message-item">
            <Plus size={14} /> {editingIndex !== null ? 'Salvar edição' : 'Adicionar à sequência'}
          </Button>
        </div>

        <div className="mt-5 pt-5 border-t border-zinc-700">
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setScheduleNow(true)}
              className={cn(
                'px-3 py-1.5 rounded-sm text-xs font-mono border transition-colors',
                scheduleNow ? 'border-lime-500 bg-lime-500/10 text-lime-300' : 'border-zinc-700 text-zinc-400'
              )}
              data-testid="mt-send-now"
            >
              ENVIAR AGORA
            </button>
            <button
              onClick={() => setScheduleNow(false)}
              className={cn(
                'px-3 py-1.5 rounded-sm text-xs font-mono border transition-colors',
                !scheduleNow ? 'border-lime-500 bg-lime-500/10 text-lime-300' : 'border-zinc-700 text-zinc-400'
              )}
              data-testid="mt-schedule"
            >
              AGENDAR
            </button>
          </div>
          {!scheduleNow && (
            <div>
              <Label>Data e hora</Label>
              <DateTimePicker value={scheduledAt} onChange={setScheduledAt} testId="mt-scheduled-at" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <Label>Delay mín. (s)</Label>
              <Input type="number" min={0} value={minDelay} onChange={(e) => setMinDelay(e.target.value)} data-testid="mt-min-delay" />
            </div>
            <div>
              <Label>Delay máx. (s)</Label>
              <Input type="number" min={0} value={maxDelay} onChange={(e) => setMaxDelay(e.target.value)} data-testid="mt-max-delay" />
            </div>
          </div>
        </div>

        <Button onClick={submit} disabled={busy} size="lg" className="mt-5 w-full" data-testid="mt-submit">
          {busy ? <Spinner /> : (
            <>
              <PaperPlaneTilt size={16} weight="fill" />
              {targetMode === 'broadcast'
                ? scheduleNow
                  ? `Disparar broadcast para ${broadcastPreview?.count ?? '?'} leads`
                  : `Agendar broadcast para ${broadcastPreview?.count ?? '?'} leads`
                : targetMode === 'manual'
                  ? scheduleNow
                    ? `Disparar para ${manualRecipients.length} números`
                    : `Agendar para ${manualRecipients.length} números`
                : scheduleNow
                  ? `Disparar para ${selectedJids.size} grupos`
                  : `Agendar para ${selectedJids.size} grupos`}
            </>
          )}
        </Button>
      </Surface>

      <Surface className="xl:col-span-2 p-5">
        {targetMode === 'groups' ? (
          <>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg text-zinc-50">Grupos da campanha</h3>
              <button onClick={toggleAll} className="text-xs font-mono text-lime-400 hover:text-lime-300" data-testid="mt-toggle-all">
                {selectedJids.size === groups.length ? 'desmarcar' : 'todos'}
              </button>
            </div>
            <p className="text-xs font-mono text-zinc-400 mb-3">{selectedJids.size} / {groups.length} selecionados</p>
            <div className="max-h-[460px] overflow-y-auto border border-zinc-700 rounded-sm divide-y divide-zinc-900">
              {groups.map((g) => {
                const checked = selectedJids.has(g.groupJid);
                return (
                  <label
                    key={g.groupJid}
                    className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-900/40 cursor-pointer"
                    data-testid={`mt-target-${shortJid(g.groupJid)}`}
                  >
                    <input type="checkbox" checked={checked} onChange={() => toggle(g.groupJid)} className="accent-lime-500" />
                    <span className="text-zinc-200 truncate flex-1">{g.subject || 'Sem nome'}</span>
                  </label>
                );
              })}
            </div>
          </>
        ) : targetMode === 'broadcast' ? (
          <>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg text-zinc-50">Leads dos grupos</h3>
              <Badge variant="warn">privado</Badge>
            </div>
            <p className="text-xs font-mono text-zinc-400 mb-3">
              Envia uma DM individual para participantes únicos dos grupos desta campanha. Duplicados são removidos automaticamente.
            </p>
            <label className="flex items-center justify-between gap-3 border border-zinc-700 bg-zinc-950 px-3 py-3 rounded-sm cursor-pointer mb-3">
              <span className="text-sm text-zinc-300">Ignorar administradores</span>
              <input
                type="checkbox"
                checked={excludeAdmins}
                onChange={(e) => {
                  setExcludeAdmins(e.target.checked);
                  setBroadcastPreview(null);
                }}
                className="accent-lime-500 w-4 h-4"
              />
            </label>
            <Button variant="secondary" onClick={loadBroadcastTargets} disabled={broadcastLoading} className="w-full mb-3">
              {broadcastLoading ? <Spinner /> : <><Users size={14} /> Atualizar leads do broadcast</>}
            </Button>
            <Surface className="p-4 bg-zinc-950/50">
              <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400">Leads únicos</p>
              <p className="font-display font-black text-4xl text-zinc-50 mt-1">{broadcastPreview?.count ?? '—'}</p>
              <p className="text-xs font-mono text-zinc-500 mt-1">{broadcastPreview ? `${broadcastPreview.groups?.length || 0} grupos analisados` : 'clique em atualizar antes de disparar'}</p>
            </Surface>
            {!!broadcastPreview?.targets?.length && (
              <div className="max-h-[280px] overflow-y-auto border border-zinc-700 rounded-sm divide-y divide-zinc-900 mt-3">
                {broadcastPreview.targets.slice(0, 80).map((target) => (
                  <div key={target.jid} className="px-3 py-2 text-xs font-mono text-zinc-300">
                    <div>{target.subject || target.jid}</div>
                    <div className="text-zinc-500 truncate">{target.sourceGroupName || 'grupo'}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg text-zinc-50">Broadcast lista manual</h3>
              <Badge variant="warn">1x1</Badge>
            </div>
            <p className="text-xs font-mono text-zinc-400 mb-3">
              Cole telefones ou suba CSV/TXT. O disparo vai no privado, um contato por vez, com delay entre leads.
            </p>
            <label className="flex items-center justify-center gap-2 border border-dashed border-zinc-700 bg-zinc-950 hover:border-lime-500/60 cursor-pointer rounded-sm px-3 py-4 text-sm text-zinc-400 transition-colors mb-3">
              <UploadSimple size={16} />
              <span>{manualFileName || 'Subir CSV/TXT'}</span>
              <input
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                onChange={(e) => {
                  const input = e.currentTarget;
                  loadManualRecipientsFile(input.files?.[0]);
                  input.value = '';
                }}
                className="hidden"
              />
            </label>
            <Textarea
              rows={8}
              value={manualRecipientsRaw}
              onChange={(e) => setManualRecipientsRaw(e.target.value)}
              placeholder="5511999999999&#10;5521988888888&#10;ou cole uma coluna de CSV"
              data-testid="manual-broadcast-recipients"
            />
            <Surface className="p-4 bg-zinc-950/50 mt-3">
              <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400">Telefones únicos válidos</p>
              <p className="font-display font-black text-4xl text-zinc-50 mt-1">{manualRecipients.length}</p>
              <p className="text-xs font-mono text-zinc-500 mt-1">
                Delay atual: {minDelay}s a {maxDelay}s entre cada lead
              </p>
            </Surface>
            {manualRecipients.length > 0 && (
              <div className="max-h-[220px] overflow-y-auto border border-zinc-700 rounded-sm divide-y divide-zinc-900 mt-3">
                {manualRecipients.slice(0, 80).map((phone) => (
                  <div key={phone} className="px-3 py-2 text-xs font-mono text-zinc-300">
                    {phone}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Surface>

      <div className="xl:col-span-5 text-xs text-zinc-400">
        O histórico de disparos desta campanha agora fica na aba <button className="text-lime-400 hover:text-lime-300" onClick={() => setTab?.('actions')}>Ações</button>.
      </div>
    </div>
  );
}

/* ========================== AUTOMATIONS TAB ========================== */
function AutomationsTab({ camp, notify }) {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setAutomations(await api.listAutomations(camp.id));
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [camp.id]);

  const toggleEnabled = async (item) => {
    try {
      await api.updateAutomation(camp.id, item.id, { enabled: !item.enabled });
      notify(item.enabled ? 'Automação pausada' : 'Automação ativada', 'success');
      load();
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remover automação "${item.name}"?`)) return;
    try {
      await api.deleteAutomation(camp.id, item.id);
      notify('Automação removida', 'success');
      load();
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-lg text-zinc-50">Automações</h3>
          <p className="text-xs text-zinc-400 font-mono">gatilhos operacionais da campanha</p>
        </div>
        <Button variant="secondary" onClick={() => setShowEditor(true)} data-testid="add-automation-btn">
          <Plus size={14} />
          Adicionar
        </Button>
      </div>
      {loading ? (
        <div className="text-zinc-400 flex items-center gap-2"><Spinner /> carregando automações…</div>
      ) : automations.length === 0 ? (
        <EmptyState icon={Lightning} title="Nenhuma automação criada" hint="Crie gatilhos para grupo cheio, entrada ou saída de leads." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {automations.map((item) => (
            <Surface key={item.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-lime-400 mb-2">
                    <Lightning size={16} weight="fill" />
                    <h4 className="font-display font-bold text-zinc-50">{item.name}</h4>
                  </div>
                  <p className="text-xs text-zinc-400 font-mono">
                    {triggerLabel(item.trigger)} · {(item.items || []).length > 1 ? `${item.items.length} mensagens` : item.type}
                  </p>
                </div>
                <Badge variant={item.enabled ? 'success' : 'default'}>{item.enabled ? 'ativa' : 'pausada'}</Badge>
              </div>
              <p className="text-sm text-zinc-400 mt-4 line-clamp-3">
                {(item.items || []).length > 0
                  ? (item.items || []).map((part) => part.text || part.caption || part.media).filter(Boolean).join(' / ')
                  : item.text || item.caption || item.media}
              </p>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="secondary" onClick={() => toggleEnabled(item)}>
                  {item.enabled ? 'Pausar' : 'Ativar'}
                </Button>
                <Button size="sm" variant="danger" onClick={() => remove(item)}>
                  Remover
                </Button>
              </div>
            </Surface>
          ))}
        </div>
      )}
      {showEditor && (
        <AutomationEditorModal
          campaignId={camp.id}
          onClose={() => setShowEditor(false)}
          onDone={() => {
            setShowEditor(false);
            notify('Automação criada', 'success');
            load();
          }}
          onError={(m) => notify(m, 'error')}
        />
      )}
    </div>
  );
}

function triggerLabel(trigger) {
  return {
    group_full: 'Grupo cheio',
    lead_join: 'Entrou no grupo',
    lead_leave: 'Saiu do grupo',
  }[trigger] || trigger;
}

function AutomationEditorModal({ campaignId, onClose, onDone, onError }) {
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState('lead_join');
  const [items, setItems] = useState([]);
  const [type, setType] = useState('text');
  const [text, setText] = useState('');
  const [mentionAll, setMentionAll] = useState(false);
  const [media, setMedia] = useState('');
  const [caption, setCaption] = useState('');
  const [mediatype, setMediatype] = useState('image');
  const [delaySeconds, setDelaySeconds] = useState(1);
  const [busy, setBusy] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const clearDraft = () => {
    setText('');
    setMentionAll(false);
    setMedia('');
    setCaption('');
    setEditingIndex(null);
  };

  const loadItemToDraft = (item) => {
    if (item.type === 'text') {
      setType('text');
      setText(item.text || '');
      setMentionAll(Boolean(item.mentionAll));
      setMedia('');
      setCaption('');
      return;
    }
    setType(item.type === 'audio' ? 'audio' : 'media');
    setText('');
    setMentionAll(false);
    setMedia(item.media || '');
    setCaption(item.caption || item.text || '');
    setMediatype(item.mediatype || 'image');
  };

  const editItem = (index) => {
    loadItemToDraft(items[index]);
    setEditingIndex(index);
  };

  const buildDraftItem = () => {
    if (type === 'text') {
      if (!text.trim()) return null;
      return { type: 'text', text: text.trim(), mentionAll };
    }
    if (!media.trim()) return null;
    return {
      type,
      text: type === 'text' ? text : caption || undefined,
      mediatype: type === 'media' ? mediatype : undefined,
      media: media.trim(),
      caption: type === 'media' ? caption || undefined : undefined,
    };
  };

  const addDraftToSequence = () => {
    const item = buildDraftItem();
    if (!item) return onError(type === 'text' ? 'Texto é obrigatório' : 'Mídia é obrigatória');
    if (editingIndex === null && items.length >= 10) return onError('Use no máximo 10 mensagens por automação');
    setItems((prev) => {
      if (editingIndex === null) return [...prev, item];
      return prev.map((old, index) => (index === editingIndex ? item : old));
    });
    clearDraft();
    setType('text');
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    if (editingIndex === index) clearDraft();
    else if (editingIndex !== null && index < editingIndex) setEditingIndex((value) => value - 1);
  };

  const draftItem = buildDraftItem();
  const sequenceItems = draftItem && editingIndex === null ? [...items, draftItem] : items;

  const submit = async () => {
    if (!name.trim()) return onError('Dê um nome para a automação');
    if (sequenceItems.length === 0) return onError('Adicione ao menos uma mensagem');
    setBusy(true);
    try {
      await api.createAutomation(campaignId, {
        name: name.trim(),
        trigger,
        enabled: true,
        type: sequenceItems[0].type,
        text: sequenceItems[0].text,
        mentionAll: sequenceItems[0].mentionAll,
        mediatype: sequenceItems[0].mediatype,
        media: sequenceItems[0].media,
        caption: sequenceItems[0].caption,
        items: sequenceItems,
        delaySeconds: Number(delaySeconds) || 0,
      });
      onDone();
    } catch (e) {
      onError(`Erro: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Nova automação"
      testId="automation-editor-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="automation-submit">
            {busy ? <Spinner /> : 'Criar automação'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <Label>Nome</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Boas-vindas no grupo" data-testid="automation-name" />
        </div>

        <SequenceList items={items} onEdit={editItem} onRemove={removeItem} testId="automation-sequence-list" />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Gatilho</Label>
            <select value={trigger} onChange={(e) => setTrigger(e.target.value)} className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm px-3 py-2 rounded-sm font-mono" data-testid="automation-trigger">
              <option value="lead_join">Entrou no grupo</option>
              <option value="lead_leave">Saiu do grupo</option>
              <option value="group_full">Grupo cheio</option>
            </select>
          </div>
          <div>
            <Label>Tipo</Label>
            <select value={type} onChange={(e) => setType(e.target.value)} className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm px-3 py-2 rounded-sm font-mono" data-testid="automation-type">
              <option value="text">Texto</option>
              <option value="media">Mídia</option>
              <option value="audio">Áudio</option>
            </select>
          </div>
        </div>
        {type === 'text' ? (
          <div className="space-y-3">
            <Label>Mensagem</Label>
            <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} data-testid="automation-text" />
            <label className="flex items-start gap-3 rounded-sm border border-zinc-800 bg-zinc-950/70 px-3 py-2 text-sm text-zinc-300">
              <input
                type="checkbox"
                checked={mentionAll}
                onChange={(e) => setMentionAll(e.target.checked)}
                className="mt-1 h-4 w-4 accent-lime-500"
                data-testid="automation-mention-all"
              />
              <span>
                <span className="block font-semibold text-zinc-100">Mencionar todos no grupo</span>
                <span className="block text-xs text-zinc-500">Funciona quando a automação enviar para um grupo.</span>
              </span>
            </label>
          </div>
        ) : (
          <div className="space-y-3">
            {type === 'media' && (
              <div>
                <Label>Tipo de mídia</Label>
                <select value={mediatype} onChange={(e) => setMediatype(e.target.value)} className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm px-3 py-2 rounded-sm font-mono">
                  <option value="image">Imagem</option>
                  <option value="video">Vídeo</option>
                  <option value="document">Arquivo</option>
                </select>
              </div>
            )}
            <div>
              <Label>URL ou base64</Label>
              <Textarea rows={2} value={media} onChange={(e) => setMedia(e.target.value)} />
            </div>
            {type === 'media' && (
              <div>
                <Label>Legenda</Label>
                <Textarea rows={2} value={caption} onChange={(e) => setCaption(e.target.value)} />
              </div>
            )}
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-zinc-400">
            {editingIndex !== null
              ? `Editando o item ${editingIndex + 1}. Salve para atualizar a sequência.`
              : items.length > 0
              ? 'O rascunho atual também será enviado no final se tiver conteúdo.'
              : 'Monte uma sequência como texto + mídia + texto.'}
          </p>
          <Button variant="secondary" onClick={addDraftToSequence} disabled={!draftItem || (editingIndex === null && items.length >= 10)}>
            <Plus size={14} /> {editingIndex !== null ? 'Salvar edição' : 'Adicionar'}
          </Button>
        </div>
        <div>
          <Label>Delay antes de enviar (s)</Label>
          <Input type="number" min={0} max={300} value={delaySeconds} onChange={(e) => setDelaySeconds(e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

/* ========================== ACTIONS TAB ========================== */
function ActionsTab({ camp, onChange, notify, setTab }) {
  const [actions, setActions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [viewingDispatch, setViewingDispatch] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [dispatchLogs, setDispatchLogs] = useState([]);

  const load = async () => {
    try {
      setActions(await api.listScheduledActions(camp.id, 500));
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    }
    try {
      const logs = await api.listLogs({ instance: camp.instanceName, limit: 500 });
      setDispatchLogs(logs.filter((l) => l.campaignId === camp.id));
    } catch (e) {
      // não bloqueia a tela de ações se os logs falharem
    }
  };

  const cancelDispatch = async (dispatchId) => {
    try {
      await api.cancelDispatch(dispatchId);
      notify('Sequência cancelada', 'success');
      onChange?.();
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [camp.id]);

  const filtered = actions.filter((a) => filter === 'all' || a.status === filter);

  const runNow = async (action) => {
    try {
      await api.runScheduledActionNow(camp.id, action.id);
      notify('Ação executada', 'success');
      load();
      onChange?.();
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    }
  };
  const cancel = async (action) => {
    try {
      await api.cancelScheduledAction(camp.id, action.id);
      notify('Ação cancelada', 'success');
      load();
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    }
  };
  const remove = async (action) => {
    if (!window.confirm(`Excluir "${action.name}"?`)) return;
    try {
      await api.deleteScheduledAction(camp.id, action.id);
      notify('Ação excluída', 'success');
      load();
    } catch (e) {
      notify(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    }
  };
  const duplicateAction = (action) => {
    setDuplicate(action);
    setShowModal(true);
  };

  const quickActions = [
    ['send_message', 'Enviar mensagem'],
    ['change_subject', 'Trocar nome'],
    ['change_description', 'Trocar descrição'],
    ['change_picture', 'Trocar imagem'],
    ['change_settings', 'Trocar configurações'],
    ['add_admins', 'Adicionar administradores'],
    ['remove_admins', 'Remover administradores'],
    ['remove_participants', 'Remover participantes'],
    ['refresh_invites', 'Atualizar links'],
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display font-bold text-lg text-zinc-50">Ações</h3>
          <p className="text-xs text-zinc-400 font-mono">fila operacional, agendamentos e execuções</p>
        </div>
        <Button onClick={() => { setDuplicate(null); setShowModal(true); }} data-testid="add-action-btn">
          <Plus size={14} /> Adicionar
        </Button>
      </div>

      <Surface className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
          {quickActions.map(([id, label]) => (
            <Button key={id} variant="secondary" onClick={() => { setDuplicate({ actionType: id, name: label, payload: {} }); setShowModal(true); }}>
              {label}
            </Button>
          ))}
        </div>
      </Surface>

      <div>
        <h4 className="font-display font-bold text-zinc-100 mb-3">Sequências de mensagens</h4>
        {(!camp.dispatches || camp.dispatches.length === 0) ? (
          <EmptyState icon={PaperPlaneTilt} title="Nenhuma sequência disparada ainda" hint="Crie e agende mensagens na aba Mensagens — o progresso de envio aparece aqui." />
        ) : (
          <div className="space-y-3">
            {camp.dispatches.map((d) => (
              <DispatchSequenceCard
                key={d.id}
                dispatch={d}
                logs={dispatchLogs}
                onCancel={cancelDispatch}
                onView={() => setViewingDispatch(d)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          ['all', 'Visão geral'],
          ['pending', 'Agendado'],
          ['running', 'Processando'],
          ['done', 'Sucesso'],
          ['failed', 'Falhas'],
          ['cancelled', 'Canceladas'],
        ].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setFilter(id)}
            className={cn(
              'px-3 py-2 text-xs font-mono border-b-2',
              filter === id ? 'border-lime-500 text-lime-300' : 'border-transparent text-zinc-400 hover:text-zinc-200'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Sparkle} title="Nenhuma ação nesta visão" hint="Adicione uma ação para executar agora ou agendar para depois." />
      ) : (
        <div className="space-y-3">
          {filtered.map((action) => (
            <div key={action.id} className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5 transition-colors hover:border-zinc-700">
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                  <span className={cn(
                    'grid h-9 w-9 shrink-0 place-items-center rounded-[10px]',
                    ['remove_admins', 'remove_participants'].includes(action.actionType) ? 'bg-red-500/10 text-red-300' : 'bg-lime-400/10 text-lime-300'
                  )}>
                    <Sparkle size={18} />
                  </span>
                  <div className="min-w-0">
                    <h4 className="truncate font-display text-lg font-bold text-zinc-100">{action.name}</h4>
                    <p className="mt-1 text-xs text-zinc-500">{actionLabel(action.actionType)}</p>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap justify-end gap-2">
                  <Badge variant={action.status === 'done' ? 'success' : action.status === 'failed' ? 'danger' : action.status === 'pending' ? 'warn' : 'default'}>
                    {action.status}
                  </Badge>
                  {action.status === 'pending' && (
                    <>
                      <Button size="sm" variant="secondary" onClick={() => runNow(action)}>Enviar agora</Button>
                      <Button size="sm" variant="danger" onClick={() => cancel(action)}>Cancelar</Button>
                    </>
                  )}
                  {action.status === 'failed' && <Button size="sm" variant="secondary" onClick={() => runNow(action)}>Reprocessar</Button>}
                  <Button size="sm" variant="secondary" onClick={() => duplicateAction(action)}>Duplicar</Button>
                  <Button size="sm" variant="danger" onClick={() => remove(action)}>Excluir</Button>
                </div>
              </div>

              <div className="mt-4 grid gap-5 lg:grid-cols-[1.6fr_1fr]">
                <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
                  <ActionTimelineContent action={action} />
                </div>
                <div className="flex flex-col justify-center gap-2 text-xs text-zinc-400">
                  <TimelineMeta label="criada" value={fmtDate(action.createdAt)} />
                  <TimelineMeta label="agenda" value={action.scheduledAt ? fmtDate(action.scheduledAt) : 'executar agora'} accent />
                  {action.finishedAt && <TimelineMeta label="fim" value={fmtDate(action.finishedAt)} />}
                  {action.error && <div className="line-clamp-2 text-red-300">erro: {action.error}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-xs text-zinc-400">
        O histórico detalhado de tudo que aconteceu fica em <button className="text-lime-400 hover:text-lime-300" onClick={() => setTab('logs')}>Logs</button>.
      </div>

      {showModal && (
        <ActionEditorModal
          camp={camp}
          seed={duplicate}
          onClose={() => setShowModal(false)}
          onDone={() => {
            setShowModal(false);
            setDuplicate(null);
            notify('Ação salva', 'success');
            load();
            onChange?.();
          }}
          onError={(m) => notify(m, 'error')}
        />
      )}

      {viewingDispatch && (
        <DispatchContentModal
          dispatch={viewingDispatch}
          logs={dispatchLogs}
          onClose={() => setViewingDispatch(null)}
        />
      )}
    </div>
  );
}

function DispatchSequenceCard({ dispatch, logs, onCancel, onView }) {
  const matching = (logs || []).filter((l) => l.dispatchId === dispatch.id);
  const sentCount = matching.filter((l) => l.status === 'sent').length;
  const failedCount = matching.filter((l) => l.status === 'failed').length;
  const itemsCount = (dispatch.items || []).length || 1;
  const targetsCount = (dispatch.targets || []).length;
  const targetLabel = dispatch.targetKind === 'lead' ? 'leads' : 'grupos';
  const total = itemsCount * targetsCount;
  const timestamps = matching
    .map((l) => l.sentAt)
    .filter(Boolean)
    .sort();
  const firstItem = (dispatch.items && dispatch.items[0]) || dispatch;
  const title = sequenceItemSummary(firstItem) || 'Sequência de mensagens';
  const finished = dispatch.finishedAt || timestamps[timestamps.length - 1];
  const statusVariant = dispatch.status === 'done' ? 'success' : dispatch.status === 'failed' ? 'danger' : dispatch.status === 'pending' ? 'warn' : 'default';

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5 transition-colors hover:border-zinc-700">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-lime-400/10 text-lime-300">
            <PaperPlaneTilt size={18} />
          </span>
          <div className="min-w-0">
            <h4 className="truncate font-display text-lg font-bold text-zinc-100">Enviar mensagem</h4>
            <p className="mt-1 text-xs text-zinc-500">{targetsCount} {targetLabel} · {itemsCount} {itemsCount === 1 ? 'parte' : 'partes'}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge variant={statusVariant}>{dispatch.status}</Badge>
          <Button size="sm" variant="secondary" onClick={onView} data-testid={`view-dispatch-${dispatch.id}`}>
            Ver tudo
          </Button>
          {dispatch.status === 'pending' && (
            <Button size="sm" variant="danger" onClick={() => onCancel(dispatch.id)} data-testid={`cancel-dispatch-${dispatch.id}`}>
              Cancelar
            </Button>
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-5 lg:grid-cols-[1.6fr_1fr]">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
          <TimelineContentPreview item={firstItem} title={title} />
          {itemsCount > 1 && (
            <p className="mt-3 text-xs font-mono text-zinc-500">+ {itemsCount - 1} parte(s) na sequência</p>
          )}
        </div>
        <div className="flex flex-col justify-center gap-2 text-xs text-zinc-400">
          <TimelineMeta label="criado" value={fmtDate(dispatch.createdAt)} />
          <TimelineMeta label="agenda" value={dispatch.scheduledAt ? fmtDate(dispatch.scheduledAt) : 'imediato'} accent />
          {finished && <TimelineMeta label="concluído" value={fmtDate(finished)} />}
          <div className="mt-1 text-sm text-zinc-300">
            {sentCount}/{total} entregues{failedCount > 0 && <span className="text-red-400"> · {failedCount} falharam</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelineMeta({ label, value, accent = false }) {
  return (
    <div className="font-mono">
      <span className="text-zinc-500">{label}: </span>
      <span className={accent ? 'text-lime-300' : 'text-zinc-300'}>{value || '—'}</span>
    </div>
  );
}

function TimelineContentPreview({ item, title }) {
  if (!item) return <p className="text-sm text-zinc-400">Sem conteúdo salvo.</p>;
  const media = item.media || item.mediaPreview || '';
  if (item.type === 'text') {
    return <p className="text-sm leading-relaxed text-zinc-200 whitespace-pre-wrap">“{item.text || title}”</p>;
  }
  if (item.type === 'audio') {
    return (
      <div className="flex items-center gap-4">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-lime-400 text-zinc-950">
          <PaperPlaneTilt size={15} weight="fill" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex h-7 items-end gap-1">
            {Array.from({ length: 22 }).map((_, i) => (
              <span key={i} className="w-1 rounded-full bg-lime-400/20" style={{ height: `${18 + ((i * 13) % 70)}%` }} />
            ))}
          </div>
          <p className="mt-2 truncate font-mono text-xs text-zinc-500">{item.fileName || item.mimetype || 'áudio enviado'}</p>
        </div>
      </div>
    );
  }
  if (item.mediatype === 'image' || item.mediatype === 'video') {
    return (
      <div className="flex items-center gap-4">
        <div className="relative h-16 w-24 shrink-0 overflow-hidden rounded-[10px] border border-zinc-700 bg-zinc-950">
          {media && !media.startsWith('data:') ? (
            <img src={media} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="grid h-full place-items-center text-[10px] text-zinc-600">sem prévia</div>
          )}
          {item.mediatype === 'video' && (
            <span className="absolute inset-0 grid place-items-center bg-black/20">
              <span className="grid h-7 w-7 place-items-center rounded-full bg-black/60 text-white">
                ▶
              </span>
            </span>
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate font-mono text-sm text-zinc-200">{item.fileName || title || 'mídia enviada'}</p>
          {(item.caption || item.text) && <p className="mt-1 line-clamp-2 text-sm text-zinc-400">{item.caption || item.text}</p>}
        </div>
      </div>
    );
  }
  return <p className="font-mono text-sm text-zinc-300">{item.fileName || title || 'arquivo enviado'}</p>;
}

function DispatchContentModal({ dispatch, logs, onClose }) {
  const [detail, setDetail] = useState(dispatch);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    api.getDispatch(dispatch.id)
      .then((data) => {
        if (alive) setDetail(data);
      })
      .catch((e) => {
        if (alive) setError(apiErrorMessage(e, 'Não foi possível carregar o conteúdo completo da ação'));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [dispatch.id]);

  const matching = (logs || []).filter((l) => l.dispatchId === dispatch.id);
  const source = detail || dispatch;
  const items = source.items?.length ? source.items : [source].filter((item) => item.type || item.text || item.caption || item.fileName || item.media);
  const sentCount = matching.filter((l) => l.status === 'sent').length;
  const failedCount = matching.filter((l) => l.status === 'failed').length;
  const targets = source.targets || [];

  return (
    <Modal
      open
      onClose={onClose}
      title="Conteúdo da ação"
      panelClassName="max-w-3xl"
      testId="dispatch-content-modal"
      footer={<Button variant="secondary" onClick={onClose}>Fechar</Button>}
    >
      <div className="space-y-5">
        <div className="grid md:grid-cols-4 gap-3">
          <div className="border border-zinc-700 bg-zinc-950 p-3 rounded-sm">
            <p className="text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-500">Status</p>
            <Badge variant={dispatch.status === 'done' ? 'success' : dispatch.status === 'failed' ? 'danger' : dispatch.status === 'pending' ? 'warn' : 'default'}>
              {dispatch.status || 'registrado'}
            </Badge>
          </div>
          <div className="border border-zinc-700 bg-zinc-950 p-3 rounded-sm">
            <p className="text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-500">Criado</p>
            <p className="text-sm text-zinc-200">{fmtDate(dispatch.createdAt)}</p>
          </div>
          <div className="border border-zinc-700 bg-zinc-950 p-3 rounded-sm">
            <p className="text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-500">Agenda</p>
            <p className="text-sm text-lime-300">{dispatch.scheduledAt ? fmtDate(dispatch.scheduledAt) : 'imediato'}</p>
          </div>
          <div className="border border-zinc-700 bg-zinc-950 p-3 rounded-sm">
            <p className="text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-500">Entrega</p>
            <p className="text-sm text-zinc-200">{sentCount}/{Math.max((items.length || 1) * (targets.length || 0), sentCount + failedCount)}</p>
            {failedCount > 0 && <p className="text-xs text-red-300">{failedCount} falharam</p>}
          </div>
        </div>

        <div>
          <h4 className="font-display font-bold text-zinc-100 mb-3">Sequência enviada</h4>
          {loading ? (
            <div className="border border-zinc-700 bg-zinc-950 p-4 rounded-sm text-sm text-zinc-400 flex items-center gap-2">
              <Spinner /> carregando conteúdo completo...
            </div>
          ) : error ? (
            <div className="border border-red-500/40 bg-red-500/10 p-4 rounded-sm text-sm text-red-200">
              {error}
            </div>
          ) : !items.length ? (
            <div className="border border-zinc-700 bg-zinc-950 p-4 rounded-sm text-sm text-zinc-400">
              Esta ação não possui conteúdo salvo para visualização.
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item, index) => (
                <DispatchContentItem key={`${item.type || 'item'}-${index}`} item={item} index={index} />
              ))}
            </div>
          )}
        </div>

        {targets.length > 0 && (
          <div>
            <h4 className="font-display font-bold text-zinc-100 mb-3">Destino</h4>
            <div className="max-h-36 overflow-y-auto border border-zinc-700 rounded-sm divide-y divide-zinc-800">
              {targets.map((target, index) => (
                <div key={`${target.groupJid || target.jid || index}`} className="px-3 py-2 text-sm text-zinc-300 flex items-center justify-between gap-3">
                  <span className="truncate">{target.subject || target.name || target.groupJid || target.jid}</span>
                  <span className="text-xs font-mono text-zinc-500 shrink-0">{shortJid(target.groupJid || target.jid || '')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function DispatchContentItem({ item, index }) {
  const title = sequenceItemTitle(item);
  const summary = sequenceItemSummary(item);
  const isText = item.type === 'text';
  const mediaLabel = item.fileName || item.mimetype || (item.media ? 'mídia anexada' : item.hasMedia ? 'mídia registrada sem prévia' : 'sem arquivo salvo');

  return (
    <div className="border border-zinc-700 bg-zinc-950 rounded-sm p-3">
      <div className="flex items-start gap-3">
        <span className="w-7 h-7 bg-lime-500 text-zinc-950 font-mono text-xs font-bold grid place-items-center rounded-sm shrink-0">
          {index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-display font-bold text-zinc-100">{title}</p>
            {item.mimetype && <Badge>{item.mimetype}</Badge>}
          </div>
          {isText ? (
            <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words mt-2">{item.text || item.caption || summary || 'sem texto salvo'}</p>
          ) : (
            <div className="mt-2 space-y-2">
              {item.media && <SequenceItemPreview item={item} />}
              <div className="border border-zinc-800 bg-black/30 px-3 py-2 rounded-sm">
                <p className="text-xs font-mono text-zinc-400">Arquivo</p>
                <p className="text-sm text-zinc-200 break-words">{mediaLabel}</p>
              </div>
              {(item.caption || item.text) && (
                <div className="border border-zinc-800 bg-black/30 px-3 py-2 rounded-sm">
                  <p className="text-xs font-mono text-zinc-400">Legenda</p>
                  <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words">{item.caption || item.text}</p>
                </div>
              )}
              {!item.caption && !item.text && summary && (
                <p className="text-xs text-zinc-500">{summary}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionTimelineContent({ action }) {
  const payload = action.payload || {};
  if (action.actionType === 'send_message') {
    const first = (payload.items || [payload]).find(Boolean);
    return <TimelineContentPreview item={first} title={actionPreview(action)} />;
  }
  if (action.actionType === 'change_picture') {
    const src = payload.pictureUrl || payload.media;
    return (
      <div className="flex items-center gap-4">
        <div className="h-16 w-24 shrink-0 overflow-hidden rounded-[10px] border border-zinc-700 bg-zinc-950">
          {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-[10px] text-zinc-600">sem prévia</div>}
        </div>
        <p className="min-w-0 truncate font-mono text-sm text-zinc-300">{src || 'sem imagem'}</p>
      </div>
    );
  }
  if (action.actionType === 'change_subject' || action.actionType === 'change_description') {
    return (
      <div className="flex items-center gap-3 text-sm">
        <span className="text-zinc-500 line-through">valor atual</span>
        <span className="text-lime-300">→</span>
        <span className="font-semibold text-zinc-200">{payload.subject || payload.description || 'novo valor'}</span>
      </div>
    );
  }
  if (['add_admins', 'remove_admins', 'remove_participants'].includes(action.actionType)) {
    return (
      <div className="flex flex-wrap gap-2">
        {(payload.participants || []).map((phone) => (
          <span key={phone} className="inline-flex items-center gap-2 rounded-full border border-zinc-700 bg-zinc-950 px-3 py-1.5 text-xs text-zinc-300">
            <span className="grid h-5 w-5 place-items-center rounded-full bg-lime-400/10 text-[9px] font-bold text-lime-300">{String(phone).slice(-2)}</span>
            {phone}
          </span>
        ))}
      </div>
    );
  }
  if (action.actionType === 'change_settings') {
    return <p className="font-mono text-sm text-zinc-300">{formatSettingActions(payload.settingActions, ' · ') || 'sem configuração'}</p>;
  }
  return <p className="text-sm text-zinc-400">{actionPreview(action)}</p>;
}

function settingActionLabel(action) {
  return {
    announcement: 'Fechar grupo: só admins enviam',
    not_announcement: 'Abrir grupo: todos enviam',
    locked: 'Travar informações: só admins editam',
    unlocked: 'Liberar informações: todos editam',
  }[action] || action;
}

function formatSettingActions(actions, separator = ', ') {
  return (actions || []).map(settingActionLabel).join(separator);
}

function actionLabel(type) {
  return {
    send_message: 'Enviar mensagem',
    change_subject: 'Trocar nome',
    change_description: 'Trocar descrição',
    change_picture: 'Trocar imagem',
    change_settings: 'Trocar configurações',
    add_admins: 'Adicionar administradores',
    remove_admins: 'Remover administradores',
    remove_participants: 'Remover participantes',
    refresh_invites: 'Atualizar links',
  }[type] || type;
}

function actionPreview(action) {
  const p = action.payload || {};
  if (action.actionType === 'send_message') return (p.items || []).map((i) => i.text || i.caption || i.media).filter(Boolean).join(' / ');
  if (action.actionType === 'change_subject') return p.subject;
  if (action.actionType === 'change_description') return p.description;
  if (action.actionType === 'change_picture') return p.pictureUrl;
  if (action.actionType === 'change_settings') return formatSettingActions(p.settingActions);
  if (['add_admins', 'remove_admins', 'remove_participants'].includes(action.actionType)) return (p.participants || []).join(', ');
  return 'sem prévia';
}

function ActionEditorModal({ camp, seed, onClose, onDone, onError }) {
  const [actionType, setActionType] = useState(seed?.actionType || 'send_message');
  const [name, setName] = useState(seed?.name || actionLabel(seed?.actionType || 'send_message'));
  const [scheduledAt, setScheduledAt] = useState('');
  const [messageItems, setMessageItems] = useState(seed?.payload?.items || []);
  const [messageType, setMessageType] = useState('text');
  const [editingIndex, setEditingIndex] = useState(null);
  const [text, setText] = useState(seed?.payload?.text || '');
  const [mentionAll, setMentionAll] = useState(Boolean(seed?.payload?.mentionAll));
  const [media, setMedia] = useState(seed?.payload?.media || seed?.payload?.pictureUrl || '');
  const [caption, setCaption] = useState(seed?.payload?.caption || '');
  const [fileName, setFileName] = useState(seed?.payload?.fileName || '');
  const [mimetype, setMimetype] = useState(seed?.payload?.mimetype || '');
  const [subject, setSubject] = useState(seed?.payload?.subject || '');
  const [description, setDescription] = useState(seed?.payload?.description || '');
  const [participantsRaw, setParticipantsRaw] = useState((seed?.payload?.participants || []).join(', '));
  const [settingActions, setSettingActions] = useState(new Set(seed?.payload?.settingActions || []));
  const [delaySeconds, setDelaySeconds] = useState(seed?.payload?.delaySeconds || 1);
  const [busy, setBusy] = useState(false);
  const [uploadingActionMedia, setUploadingActionMedia] = useState(false);

  const toggleSetting = (id) => {
    setSettingActions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const messageMediaKind = messageType === 'image' || messageType === 'video' || messageType === 'document' ? messageType : null;
  const acceptByMessageType = {
    image: '.jpg,.jpeg,.png,image/jpeg,image/png',
    video: '.mp4,video/mp4',
    audio: '.ogg,audio/ogg,audio/oga',
    document: '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,application/pdf,text/plain',
  };

  const clearMessageDraft = () => {
    setText('');
    setMentionAll(false);
    setMedia('');
    setCaption('');
    setFileName('');
    setMimetype('');
    setEditingIndex(null);
    setUploadingActionMedia(false);
  };

  const loadMessageItemToDraft = (item) => {
    if (item.type === 'text') {
      setMessageType('text');
      setText(item.text || '');
      setMentionAll(Boolean(item.mentionAll));
      setMedia('');
      setCaption('');
      setFileName('');
      setMimetype('');
      return;
    }
    if (item.type === 'audio') {
      setMessageType('audio');
      setText('');
      setMentionAll(false);
      setMedia(item.media || '');
      setCaption('');
      setFileName(item.fileName || '');
      setMimetype(item.mimetype || 'audio/ogg');
      return;
    }
    setMessageType(item.mediatype || 'image');
    setText('');
    setMentionAll(false);
    setMedia(item.media || '');
    setCaption(item.caption || item.text || '');
    setFileName(item.fileName || '');
    setMimetype(item.mimetype || '');
  };

  const editMessageItem = (index) => {
    loadMessageItemToDraft(messageItems[index]);
    setEditingIndex(index);
  };

  const removeMessageItem = (index) => {
    setMessageItems((prev) => prev.filter((_, i) => i !== index));
    if (editingIndex === index) clearMessageDraft();
    else if (editingIndex !== null && index < editingIndex) setEditingIndex((value) => value - 1);
  };

  const uploadMessageFile = async (file, kind = messageType) => {
    if (!file) return;
    const maxVideo = 100 * 1024 * 1024;
    const maxGeneric = 25 * 1024 * 1024;
    const nameValue = file.name || '';
    const lower = nameValue.toLowerCase();
    if (kind === 'image' && !/\.(jpe?g|png)$/.test(lower)) return onError('Imagem precisa ser JPG, JPEG ou PNG');
    if (kind === 'video' && (!lower.endsWith('.mp4') || file.size > maxVideo)) return onError('Vídeo precisa ser MP4 com até 100MB');
    if (kind === 'audio' && !lower.endsWith('.ogg')) return onError('Áudio precisa estar em OGG para melhor compatibilidade no WhatsApp');
    if (kind !== 'video' && file.size > maxGeneric) return onError('Arquivo precisa ter até 25MB');
    setMessageType(kind);
    setFileName(nameValue);
    setMimetype(file.type || (kind === 'audio' ? 'audio/ogg' : 'application/octet-stream'));
    setMedia('');
    setUploadingActionMedia(true);
    try {
      const uploaded = await api.uploadMedia(file);
      setMedia(uploaded.url);
      setFileName(uploaded.filename || nameValue);
      setMimetype(uploaded.contentType || file.type || (kind === 'audio' ? 'audio/ogg' : 'application/octet-stream'));
    } catch (e) {
      onError(`Erro ao subir arquivo: ${apiErrorMessage(e)}`);
      setFileName('');
      setMimetype('');
    } finally {
      setUploadingActionMedia(false);
    }
  };

  const buildMessageDraftItem = () => {
    if (messageType === 'text') {
      if (!text.trim()) return null;
      return { type: 'text', text: text.trim(), mentionAll };
    }
    if (!media.trim()) return null;
    return {
      type: messageType === 'audio' ? 'audio' : 'media',
      text: messageType === 'audio' ? undefined : caption || undefined,
      mediatype: messageMediaKind || undefined,
      media: media.trim(),
      caption: messageMediaKind ? caption || undefined : undefined,
      fileName: fileName || undefined,
      mimetype: mimetype || undefined,
    };
  };

  const addMessageDraftToSequence = () => {
    if (uploadingActionMedia) return onError('Aguarde o upload terminar antes de adicionar');
    const item = buildMessageDraftItem();
    if (!item) return onError(messageType === 'text' ? 'Escreva o texto' : 'Informe a mídia');
    if (editingIndex === null && messageItems.length >= 10) return onError('Use no máximo 10 mensagens por ação');
    setMessageItems((prev) => {
      if (editingIndex === null) return [...prev, item];
      return prev.map((old, index) => (index === editingIndex ? item : old));
    });
    clearMessageDraft();
    setMessageType('text');
  };

  const payloadForAction = () => {
    if (actionType === 'send_message') {
      const draft = buildMessageDraftItem();
      const items = draft && editingIndex === null ? [...messageItems, draft] : messageItems;
      return {
        items,
        type: items[0]?.type,
        text: items[0]?.text,
        mentionAll: items[0]?.mentionAll,
        mediatype: items[0]?.mediatype,
        media: items[0]?.media,
        caption: items[0]?.caption,
        fileName: items[0]?.fileName,
        mimetype: items[0]?.mimetype,
      };
    }
    if (actionType === 'change_subject') return { subject: subject.trim(), delaySeconds: Number(delaySeconds) || 1 };
    if (actionType === 'change_description') return { description: description.trim(), delaySeconds: Number(delaySeconds) || 1 };
    if (actionType === 'change_picture') return { pictureUrl: media.trim(), delaySeconds: Number(delaySeconds) || 1 };
    if (actionType === 'change_settings') return { settingActions: Array.from(settingActions), delaySeconds: Number(delaySeconds) || 1 };
    if (['add_admins', 'remove_admins', 'remove_participants'].includes(actionType)) {
      return { participants: parsePhoneList(participantsRaw), delaySeconds: Number(delaySeconds) || 1 };
    }
    return {};
  };

  const validate = (payload) => {
    if (!name.trim()) return 'Informe o nome da ação';
    if (actionType === 'send_message' && (!payload.items || payload.items.length === 0)) return 'Informe texto ou imagem da mensagem';
    if (actionType === 'change_subject' && !payload.subject) return 'Informe o novo nome';
    if (actionType === 'change_description' && !payload.description) return 'Informe a nova descrição';
    if (actionType === 'change_picture' && !payload.pictureUrl) return 'Informe a URL da imagem';
    if (actionType === 'change_settings' && (!payload.settingActions || payload.settingActions.length === 0)) return 'Escolha ao menos uma configuração';
    if (['add_admins', 'remove_admins', 'remove_participants'].includes(actionType) && payload.participants.length === 0) return 'Informe os números';
    return '';
  };

  const submit = async () => {
    if (actionType === 'send_message' && uploadingActionMedia) return onError('Aguarde o upload da mídia terminar');
    const payload = payloadForAction();
    const error = validate(payload);
    if (error) return onError(error);
    setBusy(true);
    try {
      await api.createScheduledAction(camp.id, {
        name: name.trim(),
        actionType,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        payload,
      });
      onDone();
    } catch (e) {
      onError(`Erro: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Adicionar ação"
      testId="action-editor-modal"
      panelClassName="max-w-2xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="save-action-btn">{busy ? <Spinner /> : 'Salvar ação'}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Tipo de ação</Label>
            <select value={actionType} onChange={(e) => { setActionType(e.target.value); setName(actionLabel(e.target.value)); }} className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm px-3 py-2 rounded-sm font-mono">
              <option value="send_message">Enviar mensagem</option>
              <option value="change_subject">Trocar nome</option>
              <option value="change_description">Trocar descrição</option>
              <option value="change_picture">Trocar imagem</option>
              <option value="change_settings">Trocar configurações</option>
              <option value="add_admins">Adicionar administradores</option>
              <option value="remove_admins">Remover administradores</option>
              <option value="remove_participants">Remover participantes</option>
              <option value="refresh_invites">Atualizar links dos grupos</option>
            </select>
          </div>
          <div>
            <Label>Quando</Label>
            <DateTimePicker value={scheduledAt} onChange={setScheduledAt} />
          </div>
        </div>

        <div>
          <Label>Nome da ação</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        {actionType === 'send_message' && (
          <div className="space-y-4">
            <SequenceList items={messageItems} onEdit={editMessageItem} onRemove={removeMessageItem} testId="action-message-sequence-list" />

            <div className="grid grid-cols-5 gap-2">
              {[
                ['text', 'Texto'],
                ['image', 'Imagem'],
                ['video', 'Vídeo'],
                ['audio', 'Áudio'],
                ['document', 'Arquivo'],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    setMessageType(id);
                    setMedia('');
                    setFileName('');
                    setMimetype('');
                  }}
                  className={cn(
                    'px-3 py-2 rounded-sm border text-xs transition-colors',
                    messageType === id ? 'border-lime-500 bg-lime-500/10 text-lime-300' : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:text-zinc-100'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {messageType === 'text' ? (
              <div className="space-y-3">
                <Label>Mensagem de texto</Label>
                <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} />
                <label className="flex items-start gap-3 rounded-sm border border-zinc-800 bg-zinc-950/70 px-3 py-2 text-sm text-zinc-300">
                  <input
                    type="checkbox"
                    checked={mentionAll}
                    onChange={(e) => setMentionAll(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-lime-500"
                    data-testid="action-mention-all"
                  />
                  <span>
                    <span className="block font-semibold text-zinc-100">Mencionar todos no grupo</span>
                    <span className="block text-xs text-zinc-500">Usa @todos ao executar esta ação nos grupos da campanha.</span>
                  </span>
                </label>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label>Arquivo</Label>
                  <label className="flex items-center justify-center gap-2 border border-dashed border-zinc-700 bg-zinc-950 hover:border-lime-500/60 cursor-pointer rounded-sm px-3 py-4 text-sm text-zinc-400 transition-colors">
                    <UploadSimple size={16} />
                    <span>{uploadingActionMedia ? 'Subindo arquivo...' : fileName || 'Escolher arquivo'}</span>
                    <input
                      type="file"
                      accept={acceptByMessageType[messageType]}
                      onChange={(e) => {
                        const input = e.currentTarget;
                        uploadMessageFile(input.files?.[0], messageType).finally(() => {
                          input.value = '';
                        });
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
                <div>
                  <Label>URL da mídia</Label>
                  <Textarea rows={2} value={media} onChange={(e) => setMedia(e.target.value)} placeholder="https://..." />
                </div>
                {messageType !== 'audio' && (
                  <div>
                    <Label>Texto / legenda (opcional)</Label>
                    <Textarea rows={2} value={caption} onChange={(e) => setCaption(e.target.value)} />
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-zinc-400">
                {editingIndex !== null
                  ? `Editando o item ${editingIndex + 1}. Salve para atualizar a sequência.`
                  : messageItems.length > 0
                  ? 'O rascunho atual também será enviado no final se tiver conteúdo.'
                  : 'Monte uma sequência como texto + mídia + texto.'}
              </p>
              <Button variant="secondary" onClick={addMessageDraftToSequence} disabled={uploadingActionMedia || !buildMessageDraftItem() || (editingIndex === null && messageItems.length >= 10)}>
                <Plus size={14} /> {editingIndex !== null ? 'Salvar edição' : 'Adicionar'}
              </Button>
            </div>
          </div>
        )}

        {actionType === 'change_subject' && <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Novo nome dos grupos" />}
        {actionType === 'change_description' && <Textarea rows={5} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Nova descrição" />}
        {actionType === 'change_picture' && <Input value={media} onChange={(e) => setMedia(e.target.value)} placeholder="https://.../imagem.jpg" />}
        {actionType === 'change_settings' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              ['announcement', 'Só admins enviam'],
              ['not_announcement', 'Todos enviam'],
              ['locked', 'Só admins editam infos'],
              ['unlocked', 'Todos editam infos'],
            ].map(([id, label]) => (
              <ToggleRow key={id} checked={settingActions.has(id)} onChange={() => toggleSetting(id)}>
                {label}
              </ToggleRow>
            ))}
          </div>
        )}
        {['add_admins', 'remove_admins', 'remove_participants'].includes(actionType) && (
          <div>
            <Label>Números</Label>
            <Textarea rows={3} value={participantsRaw} onChange={(e) => setParticipantsRaw(e.target.value)} placeholder="5511999999999, um por linha ou separado por vírgula" />
          </div>
        )}

        {actionType !== 'send_message' && actionType !== 'refresh_invites' && (
          <div>
            <Label>Delay entre grupos (s)</Label>
            <Input type="number" min={0} max={30} value={delaySeconds} onChange={(e) => setDelaySeconds(e.target.value)} />
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ========================== LOGS TAB ========================== */
function LogsTab({ camp }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetch = async () => {
      try {
        const [list, adminActions] = await Promise.all([
          api.listLogs({ instance: camp.instanceName, limit: 300 }),
          api.campaignActions(camp.id, 300),
        ]);
        // filter logs scoped to this campaign's groupJids
        const jids = new Set((camp.groups || []).map((g) => g.groupJid));
        const messageActions = list
          .filter((l) => jids.has(l.targetJid) || l.campaignId === camp.id)
          .map((l) => ({
            id: l.id,
            at: l.sentAt,
            status: l.status,
            type: `mensagem:${l.type}`,
            target: l.targetName || shortJid(l.targetJid),
            detail: l.status === 'failed' ? (l.error || '').toString().slice(0, 120) : l.preview,
          }));
        const systemActions = (adminActions || []).map((a) => ({
          id: a.id,
          at: a.createdAt,
          status: a.status,
          type: a.action,
          target: 'campanha',
          detail: Object.entries(a.detail || {}).map(([k, v]) => `${k}: ${v}`).join(' · '),
        }));
        if (alive) setActions([...systemActions, ...messageActions].sort((a, b) => String(b.at).localeCompare(String(a.at))));
      } catch {
        /* ignore */
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetch();
    const t = setInterval(fetch, 6000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [camp.id, camp.instanceName, camp.groups]);

  if (loading) return <div className="text-zinc-400 flex items-center gap-2"><Spinner /> carregando logs…</div>;
  if (actions.length === 0) return <EmptyState title="Nenhum log registrado nesta campanha ainda" />;

  return (
    <Surface className="p-4 font-mono text-xs">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display font-bold text-lg text-zinc-50">Logs da campanha</h3>
          <p className="text-xs text-zinc-400 font-mono">envios e execuções já registradas</p>
        </div>
        <Badge variant="info">LIVE</Badge>
      </div>
      <ul className="space-y-1">
        {actions.map((l) => (
          <li key={l.id} className="grid grid-cols-12 gap-2 hover:bg-zinc-900/40 px-2 py-1.5 rounded-sm" data-testid={`action-${l.id}`}>
            <span className="col-span-2 text-zinc-400">{fmtDate(l.at)}</span>
            <span className={`col-span-1 uppercase ${statusColor(l.status)}`}>
              {l.status === 'sent' || l.status === 'done' ? '●ok' : l.status === 'failed' ? '✕fail' : l.status}
            </span>
            <span className="col-span-2 text-zinc-400">{l.type}</span>
            <span className="col-span-3 text-zinc-200 truncate">{l.target}</span>
            <span className="col-span-4 text-zinc-400 truncate">
              {l.detail}
            </span>
          </li>
        ))}
      </ul>
    </Surface>
  );
}
